import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Trash2, ReceiptText, Printer, Pencil, Minus, Maximize2, Minimize2, X, Paperclip, Eye, Download, Search, MessageCircle, Save, ShieldCheck } from "lucide-react";
import { inr, inr2, fmtDate, today } from "@/lib/format";
import { HEAD_OFFICE } from "@/lib/company";
import { printMoneyReceipt, printMoneyReceipts, printReceiptRegister, printPartPaymentSummary, printTdsRegister, printTdsYearlySummary, printChequeRegister, printDepositSlip, printDeductionRegister, printDeductionTrend, printRecoveryRegister, printBankDelayReport, printNeftOnlyList, printPaymentScores } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import { ListPagination, NewBadge } from "@/components/ListPagination";
import { logPadIssue, PadLogButton } from "@/components/PadIssueLog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const MODES = ["Cash", "Bank", "UPI", "Cheque", "NEFT", "RTGS", "DD", "IMPS", "Card"];

const MR_COPY_NAMES = ["ACCOUNT COPY", "HO COPY", "FILE COPY"];

function BlankMrDialog({ open, onClose, onPrint, existingRows }) {
  const [startNo, setStartNo] = useState("");
  const [count, setCount] = useState(1);
  const [clashes, setClashes] = useState([]);
  const [warned, setWarned] = useState(false);
  useEffect(() => { if (open) { setStartNo(""); setCount(1); setClashes([]); setWarned(false); } }, [open]);
  const go = () => {
    const n = Math.min(Math.max(Number(count) || 1, 1), 50);
    const m = String(startNo).trim().match(/^(.*?)(\d+)$/);
    const list = Array.from({ length: n }, (_, i) => {
      if (!startNo.trim()) return { blank: true };
      if (!m || n === 1) return { blank: true, mr_no: startNo.trim() };
      return { blank: true, mr_no: m[1] + String(Number(m[2]) + i).padStart(m[2].length, "0") };
    });
    const norm = (v) => String(v || "").replace(/^MR[-\s]?/i, "").trim().toLowerCase();
    const used = new Set((existingRows || []).map((r) => norm(r.mr_no)).filter(Boolean));
    const bad = list.map((l) => l.mr_no).filter((no) => no && used.has(norm(no)));
    if (bad.length && !warned) {
      setClashes(bad);
      setWarned(true);
      return;
    }
    onPrint(list);
    onClose();
  };
  const onChange = (fn) => (e) => { fn(e.target.value); setWarned(false); setClashes([]); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[380px]" data-testid="blank-mr-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Blank Money Receipt Print</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Starting MR No. (manual entry)</Label>
            <Input value={startNo} onChange={onChange(setStartNo)} placeholder="e.g. 2101 (leave blank for no number)" data-testid="blank-mr-start-no" />
          </div>
          <div className="grid gap-1.5">
            <Label>How many blank receipts</Label>
            <Input type="number" min="1" max="50" value={count} onChange={onChange(setCount)} data-testid="blank-mr-count" />
            <p className="text-xs text-muted-foreground">Serial numbers auto-increment from the starting MR No. so hand-filled receipts stay traceable.</p>
          </div>
          {clashes.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="blank-mr-clash-warning">
              ⚠ MR No. {clashes.join(", ")} already exist{clashes.length === 1 ? "s" : ""} in the system. Click "Print Anyway" to continue or change the number.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant={clashes.length ? "destructive" : "default"} onClick={go} data-testid="blank-mr-continue-btn">
            <Printer className="mr-1.5 h-4 w-4" /> {clashes.length ? "Print Anyway" : "Choose Copies"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MrCopyChoiceDialog({ target, onClose }) {
  const [picked, setPicked] = useState(MR_COPY_NAMES);
  useEffect(() => { if (target) setPicked(MR_COPY_NAMES); }, [target]);
  const toggle = (cp) => setPicked((p) => (p.includes(cp) ? p.filter((x) => x !== cp) : [...p, cp]));
  const doPrint = () => {
    const copies = MR_COPY_NAMES.filter((c) => picked.includes(c));
    if (Array.isArray(target)) {
      printMoneyReceipts(target, copies);
      if (target[0]?.blank) logPadIssue("MR", target, "mr_no");
    } else {
      printMoneyReceipt(target, copies);
    }
    onClose();
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[360px]" data-testid="mr-copy-choice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Which copies to print?</DialogTitle>
        </DialogHeader>
        <div className="grid gap-1.5">
          {MR_COPY_NAMES.map((cp) => (
            <label key={cp} className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-sm font-medium ${picked.includes(cp) ? "border-primary/40 bg-primary/5" : "border-border"}`}>
              <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={picked.includes(cp)} onChange={() => toggle(cp)} data-testid={`mr-copy-check-${cp.split(" ")[0].toLowerCase()}`} />
              {cp.charAt(0) + cp.slice(1).toLowerCase()}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={doPrint} disabled={picked.length === 0} data-testid="mr-copy-choice-print-btn">
            <Printer className="mr-1.5 h-4 w-4" /> Print {picked.length} cop{picked.length === 1 ? "y" : "ies"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
import { BRANCHES } from "@/lib/branches";
const CHARGE_KEYS = ["freight_charge", "hamali_charge", "odc_charge", "charge", "other_charges", "mr_charges", "on_account", "part_payment"];
const CHARGE_LABELS = {
  freight_charge: "Freight Charge",
  hamali_charge: "Hamali Charge",
  odc_charge: "ODC Charge",
  charge: "Charge",
  other_charges: "Other Charges",
  mr_charges: "M.R. Charges",
  on_account: "On Account",
  part_payment: "Part Payment",
};
const EMPTY = {
  mr_no: "",
  office: "Chennai",
  customer_id: "",
  customer_name: "",
  against_invoice_id: "",
  against_invoice_no: "",
  bill_date: "",
  against_cn_no: "",
  cn_date: "",
  from_place: "",
  to_place: "",
  freight_charge: 0,
  hamali_charge: 0,
  odc_charge: 0,
  charge: 0,
  other_charges: 0,
  mr_charges: 0,
  on_account: 0,
  part_payment: 0,
  tds: 0,
  tds_pct: 0,
  tds_mode: "manual",
  deduction: 0,
  deduction_reason: "",
  bills: [],
  amount: 0,
  payment_mode: "Cash",
  payment_date: "",
  cheque_no: "",
  cheque_date: "",
  cheque_amount: 0,
  bank_name: "",
  narration: "",
  date: today(),
};

const adviceUrl = (r) => `${API}/files/${r.advice_path}?auth=${localStorage.getItem("ntc_token")}`;

function AdviceCell({ row, onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/receipts/${row.id}/advice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment advice uploaded");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-1">
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`advice-file-input-${row.id}`} />
      {row.advice_path ? (
        <a
          href={adviceUrl(row)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline"
          title={row.advice_filename || "View payment advice"}
          data-testid="view-advice-link"
        >
          <Eye className="h-3.5 w-3.5" /> View
        </a>
      ) : null}
      <Button
        variant="ghost"
        size="icon"
        className={`h-7 w-7 ${row.advice_path ? "text-emerald-600" : "text-slate-400"}`}
        title={row.advice_path ? "Replace payment advice" : "Upload payment advice"}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        data-testid="upload-advice-btn"
      >
        <Paperclip className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function MoneyReceipts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const FY_OPTIONS = (() => {
    const n = new Date();
    const curFy = n.getMonth() + 1 >= 4 ? n.getFullYear() : n.getFullYear() - 1;
    return Array.from({ length: 5 }, (_, i) => curFy - i);
  })();
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [custCode, setCustCode] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [outstanding, setOutstanding] = useState({});
  const [printTarget, setPrintTarget] = useState(null);
  const [blankMrOpen, setBlankMrOpen] = useState(false);
  const [dedRegOpen, setDedRegOpen] = useState(false);
  const [dedRegMonth, setDedRegMonth] = useState(today().slice(0, 7));
  const [dedView, setDedView] = useState("register");
  const [recovOpen, setRecovOpen] = useState(false);
  const [recovMonth, setRecovMonth] = useState(today().slice(0, 7));
  const [tdsYrOpen, setTdsYrOpen] = useState(false);
  const [tdsYrFy, setTdsYrFy] = useState(String(FY_OPTIONS[0]));
  const [tdsYrMetric, setTdsYrMetric] = useState("tds");
  const YR_METRICS = { tds: "TDS", deduction: "Deduction", loading: "Loading Charge", unloading: "Unloading Charge" };
  const yrIsDed = tdsYrMetric === "deduction";
  const yrIsCharge = tdsYrMetric === "loading" || tdsYrMetric === "unloading";
  const yrLabel = YR_METRICS[tdsYrMetric];
  const fyLabel = (y) => `${y}-${String(Number(y) + 1).slice(2)}`;
  const buildTdsYearly = () => {
    const start = Number(tdsYrFy);
    const from = `${start}-04-01`;
    const to = `${start + 1}-03-31`;
    const byCust = {};
    const quarter = (d) => {
      const m = Number(d.slice(5, 7));
      return m >= 4 && m <= 6 ? 0 : m >= 7 && m <= 9 ? 1 : m >= 10 ? 2 : 3;
    };
    const push = (cust, q, val, amount) => {
      if (!(val > 0)) return;
      const g = (byCust[cust || "—"] = byCust[cust || "—"] || { customer: cust || "—", q: [0, 0, 0, 0], tds: 0, amount: 0 });
      g.q[q] += val;
      g.tds += val;
      g.amount += amount || 0;
    };
    if (yrIsCharge) {
      (invoices || []).forEach((inv) => {
        const d = String(inv.issue_date || "").slice(0, 10);
        if (!d || d < from || d > to) return;
        const val = Number(tdsYrMetric === "loading" ? inv.loading_charge : inv.unloading_charge) || 0;
        push(inv.customer_name, quarter(d), val, Number(inv.total) || 0);
      });
      return Object.values(byCust).sort((a, b) => b.tds - a.tds);
    }
    (rows || []).forEach((r) => {
      if (r.reversed) return;
      if (yrIsDed && (r.deduction_status || "") === "Rejected") return;
      const d = String(r.date || "").slice(0, 10);
      if (!d || d < from || d > to) return;
      const q = quarter(d);
      if ((r.bills || []).length) {
        r.bills.forEach((b) => push(r.customer_name, q, Number(yrIsDed ? b.deduction : b.tds) || 0, Number(b.amount) || 0));
      } else {
        const gross = CHARGE_KEYS.reduce((s, k) => s + (Number(r[k]) || 0), 0);
        push(r.customer_name, q, Number(yrIsDed ? r.deduction : r.tds) || 0, gross > 0 ? gross : Number(r.amount) || 0);
      }
    });
    return Object.values(byCust).sort((a, b) => b.tds - a.tds);
  };
  const tdsYearlyGroups = tdsYrOpen ? buildTdsYearly() : [];
  const yrBranchTotals = (() => {
    if (!tdsYrOpen || !yrIsCharge) return [];
    const start = Number(tdsYrFy);
    const from = `${start}-04-01`;
    const to = `${start + 1}-03-31`;
    const byBr = {};
    (invoices || []).forEach((inv) => {
      const d = String(inv.issue_date || "").slice(0, 10);
      if (!d || d < from || d > to) return;
      const val = Number(tdsYrMetric === "loading" ? inv.loading_charge : inv.unloading_charge) || 0;
      if (!(val > 0)) return;
      const br = inv.office || "Chennai";
      byBr[br] = (byBr[br] || 0) + val;
    });
    return Object.entries(byBr).sort((a, b) => b[1] - a[1]);
  })();
  const recovRows = (rows || [])
    .filter((r) => r.cheque_status === "Bounced" && String(r.date || "").startsWith(recovMonth))
    .map((r) => {
      const rp = r.replacement;
      const repl = rp ? `${rp.cheque_no}${rp.bank_name ? ` · ${rp.bank_name}` : ""} · ${rp.status || "Pending"}` : "";
      const recovery = rp?.status === "Cleared" ? "Recovered" : rp?.status === "Pending" ? "Replacement pending" : rp?.status === "Bounced" ? "Replacement bounced" : "Unrecovered";
      return { ...r, _repl: repl, _recovery: recovery };
    })
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const recovMonthLabel = recovMonth ? new Date(`${recovMonth}-01`).toLocaleString("en-IN", { month: "long", year: "numeric" }) : "";
  const RECOV_CLR = { Recovered: "bg-emerald-50 text-emerald-700 border-emerald-200", "Replacement pending": "bg-amber-50 text-amber-700 border-amber-200", "Replacement bounced": "bg-rose-50 text-rose-700 border-rose-200", Unrecovered: "bg-rose-50 text-rose-700 border-rose-200" };
  const [replTarget, setReplTarget] = useState(null);
  const [replForm, setReplForm] = useState({ cheque_no: "", cheque_date: "", cheque_amount: "", bank_name: "" });
  const saveReplacement = async () => {
    if (!replForm.cheque_no.trim()) {
      toast.error("Enter the replacement cheque number");
      return;
    }
    try {
      const res = await api.post(`/receipts/${replTarget.id}/replacement-cheque`, replForm);
      setRows((p) => (p || []).map((x) => (x.id === replTarget.id ? { ...x, replacement: res.data } : x)));
      setReplTarget(null);
      toast.success("Replacement cheque recorded — track it until it clears");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const setReplStatus = async (r, status) => {
    try {
      await api.put(`/receipts/${r.id}/replacement-status`, { status });
      setRows((p) => (p || []).map((x) => (x.id === r.id ? { ...x, replacement: { ...x.replacement, status } } : x)));
      toast.success(status === "Cleared" ? "Replacement cleared — bounce recovered ✔" : `Replacement marked ${status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("single");
  const [dlgView, setDlgView] = useState("normal");
  const [sel, setSel] = useState(new Set());
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [page, setPage] = useState(1);
  const [fyPick, setFyPick] = useState("");
  const [mrJump, setMrJump] = useState("");
  const [mrHl, setMrHl] = useState(null);
  const [custSearch, setCustSearch] = useState("");
  const [partOnly, setPartOnly] = useState(false);
  const rowCode = (r) =>
    r.customer_code ||
    customers.find((c) => c.id === r.customer_id)?.customer_code ||
    customers.find((c) => (c.name || "").trim().toLowerCase() === (r.customer_name || "").trim().toLowerCase())?.customer_code ||
    "";
  const billInfo = (r) => {
    const norm = (v) => String(v || "").replace(/^INV[-\s]?/i, "").replace(/^0+/, "").trim();
    if ((r.bills || []).length) {
      const nos = r.bills.map((b) => norm(b.bill_no)).filter(Boolean);
      const invs = (invoices || []).filter((i) => nos.includes(norm(i.invoice_no)));
      return {
        no: nos.join(", ") || "—",
        date: invs[0]?.issue_date || "",
        amount: invs.length ? invs.reduce((s, i) => s + (Number(i.total) || 0), 0) : r.bills.reduce((s, b) => s + (Number(b.amount) || 0), 0),
      };
    }
    const inv = (invoices || []).find((i) => i.id === r.against_invoice_id) || (invoices || []).find((i) => r.against_invoice_no && norm(i.invoice_no) === norm(r.against_invoice_no));
    return { no: inv ? norm(inv.invoice_no) : norm(r.against_invoice_no) || "—", date: inv?.issue_date || "", amount: inv ? Number(inv.total) || 0 : 0 };
  };
  const coveredAmt = (r) => (Number(r.amount) || 0) + (Number(r.tds) || 0) + (Number(r.deduction) || 0);
  const isPartPaid = (r) => {
    const ba = Number(billInfo(r).amount) || 0;
    return ba > 0 && coveredAmt(r) < ba - 0.5;
  };
  const waPartBalance = (r) => {
    const bi = billInfo(r);
    const short = (Number(bi.amount) || 0) - coveredAmt(r);
    const cust = customers.find((c) => c.id === r.customer_id) || customers.find((c) => (c.name || "").trim().toLowerCase() === (r.customer_name || "").trim().toLowerCase());
    const digits = String(cust?.phone || "").replace(/\D/g, "");
    const msg = `Dear ${r.customer_name || "Customer"},\n\nWe thank you for your part payment of ₹${(Number(r.amount) || 0).toLocaleString("en-IN")}${r.date ? ` dated ${fmtDate(r.date)}` : ""} against Bill No. ${bi.no} (Bill Amount ₹${(Number(bi.amount) || 0).toLocaleString("en-IN")}).\n\n*Balance pending: ₹${short.toLocaleString("en-IN")}*\n\nKindly arrange the balance payment at the earliest.\n\n— Nisha Transport Corporation`;
    if (!digits) toast.info(`No phone saved for ${r.customer_name || "this customer"} — add it in Customer Master. Opening WhatsApp without a number.`);
    window.open(`https://wa.me/${digits ? (digits.length === 10 ? "91" + digits : digits) : ""}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const displayRows = (rows || []).filter((r) => {
    const d = r.date || "";
    if (!((!filterFrom || d >= filterFrom) && (!filterTo || d <= filterTo))) return false;
    const q = custSearch.trim().toLowerCase();
    if (q && ![r.customer_name, rowCode(r), String(r.mr_no || "")].some((v) => String(v || "").toLowerCase().includes(q))) return false;
    if (partOnly && !isPartPaid(r)) return false;
    return true;
  });
  const sendDailyCollection = () => {
    const day = filterFrom && filterFrom === filterTo ? filterFrom : today();
    const list = (rows || []).filter((r) => (r.date || "") === day);
    if (!list.length) {
      toast.error(`No collections recorded for ${fmtDate(day)}`);
      return;
    }
    const by = (key) => {
      const m = {};
      list.forEach((r) => { const k = key(r) || "—"; m[k] = (m[k] || 0) + (Number(r.amount) || 0); });
      return Object.entries(m);
    };
    const total = list.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const msg = [
      "*NTC — Daily Collection Summary*",
      `Date: ${fmtDate(day)}`,
      "",
      "Branch-wise:",
      ...by((r) => r.office || "Chennai").map(([k, v]) => `• ${k}: ₹${v.toLocaleString("en-IN")}`),
      "",
      "Mode-wise:",
      ...by((r) => r.payment_mode || "Cash").map(([k, v]) => `• ${k}: ₹${v.toLocaleString("en-IN")}`),
      "",
      "Receipts:",
      ...list.map((r, i) => `${i + 1}. MR ${String(r.mr_no || "").replace(/^MR[-\s]?/i, "") || "—"} — ${r.customer_name || "—"}${rowCode(r) ? ` (${rowCode(r)})` : ""}: ₹${(Number(r.amount) || 0).toLocaleString("en-IN")} (${r.payment_mode || "Cash"})`),
      "",
      `*Total Collection: ₹${total.toLocaleString("en-IN")} — ${list.length} receipt(s)*`,
      "",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const mrPages = Math.max(1, Math.ceil(displayRows.length / 20));
  const curMrPage = Math.min(page, mrPages);
  const pagedReceipts = displayRows.slice((curMrPage - 1) * 20, curMrPage * 20);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [filterFrom, filterTo, custSearch]);
  const jumpToMr = () => {
    const jq = mrJump.trim().toLowerCase().replace(/^mr[-\s]?/, "");
    if (!jq) return;
    const key = (r) => String(r.mr_no || "").toLowerCase().replace(/^mr[-\s]?/, "");
    let idx = displayRows.findIndex((r) => key(r) === jq);
    if (idx === -1) idx = displayRows.findIndex((r) => key(r).includes(jq));
    if (idx === -1) {
      toast.error(`MR "${mrJump.trim()}" not found`);
      return;
    }
    const target = Math.floor(idx / 20) + 1;
    setPage(target);
    setMrHl(displayRows[idx].id);
    toast.success(`MR found on page ${target}`);
    setTimeout(() => setMrHl(null), 5000);
  };
  const selectedRows = displayRows.filter((r) => sel.has(r.id));
  const allChecked = displayRows.length > 0 && displayRows.every((r) => sel.has(r.id));
  const toggleAll = () => setSel(allChecked ? new Set() : new Set(displayRows.map((r) => r.id)));
  const toggleOne = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const loadAll = async () => {
    try {
      const [r, c, inv, out] = await Promise.all([
        api.get("/receipts"),
        api.get("/customers"),
        api.get("/invoices"),
        api.get("/reports/bill-outstanding").catch(() => ({ data: [] })),
      ]);
      setRows(r.data);
      setCustomers(c.data);
      setInvoices(inv.data);
      const map = {};
      (out.data || []).forEach((o) => { map[o.invoice_id] = o; });
      setOutstanding(map);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    loadAll();
  }, []);

  const [dedApprOpen, setDedApprOpen] = useState(false);
  const pendingDed = (rows || []).filter((r) => (r.deduction_status || "") === "Pending Approval" && !r.reversed);
  const reviewDed = async (r, approve) => {
    try {
      await api.put(`/receipts/${r.id}/deduction-approval`, { approve });
      toast.success(`Deduction ${approve ? "approved — branch MR stands for deduction payment" : "rejected"} (MR ${String(r.mr_no || "").replace(/^MR[-\s]?/i, "") || "—"})`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem("ntc_draft_receipt");
      if (raw) {
        const d = JSON.parse(raw);
        setForm(d.form || EMPTY);
        setMode(d.mode || "single");
        setEditing(d.editing || null);
        setDlgView("min");
        setOpen(true);
      }
    } catch (e) { /* ignore */ }
  }, []);

  const clearDraft = () => { try { localStorage.removeItem("ntc_draft_receipt"); } catch (e) {} };
  const saveDraft = () => { try { localStorage.setItem("ntc_draft_receipt", JSON.stringify({ form, mode, editing })); } catch (e) {} };

  const openCreate = () => {
    setEditing(null);
    setMode("single");
    setForm({ ...EMPTY, date: today() });
    setCustCode("");
    setDlgView("normal");
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setMode(row.bills && row.bills.length ? "multi" : "single");
    setForm({ ...EMPTY, ...row, bills: row.bills || [] });
    setCustCode(customers.find((c) => c.id === row.customer_id)?.customer_code || "");
    setDlgView("normal");
    setOpen(true);
  };

  const custInvoices = invoices.filter((i) => {
    const match =
      !form.customer_id ||
      i.customer_id === form.customer_id ||
      (form.customer_name && (i.customer_name === form.customer_name || i.third_party_name === form.customer_name));
    return match && i.status !== "Paid";
  });

  const applyAmount = (next) => {
    const gross = CHARGE_KEYS.reduce((s, ck) => s + (Number(next[ck]) || 0), 0);
    if (next.tds_mode === "auto") {
      next.tds = Math.round(gross * (Number(next.tds_pct) || 0)) / 100;
    }
    if (gross > 0) {
      next.amount = gross - (Number(next.tds) || 0) - (Number(next.deduction) || 0);
    }
    return next;
  };

  const blankBill = () => ({ branch: form.office || "Chennai", bill_no: "", bill_date: "", cn_no: "", cn_date: "", from_place: "", destination: "", amount: 0, tds: 0, deduction: 0 });

  const applyBillsTotal = (bills) => {
    const total = (bills || []).reduce(
      (s, b) => s + (Number(b.amount) || 0) - (Number(b.tds) || 0) - (Number(b.deduction) || 0),
      0
    );
    return total;
  };

  const addBillRow = () => setForm((f) => ({ ...f, bills: [...(f.bills || []), blankBill()] }));

  const addBillFromInvoice = (invId) => {
    const inv = invoices.find((i) => i.id === invId);
    if (!inv) return;
    const row = {
      branch: inv.office || form.office || "Chennai",
      bill_no: String(inv.invoice_no || "").replace(/^INV[-\s]?/i, ""),
      bill_date: inv.issue_date || "",
      cn_no: "",
      cn_date: "",
      from_place: inv.from_place || inv.origin || "",
      destination: inv.destination || "",
      amount: inv.total || 0,
      tds: 0,
      deduction: 0,
    };
    setForm((f) => {
      const bills = [...(f.bills || []), row];
      return { ...f, bills, amount: applyBillsTotal(bills), customer_id: f.customer_id || inv.customer_id || "", customer_name: f.customer_name || inv.customer_name || "" };
    });
  };

  const updateBillRow = (idx, key, val) =>
    setForm((f) => {
      const bills = (f.bills || []).map((b, i) => (i === idx ? { ...b, [key]: val } : b));
      return { ...f, bills, amount: applyBillsTotal(bills) };
    });

  const removeBillRow = (idx) =>
    setForm((f) => {
      const bills = (f.bills || []).filter((_, i) => i !== idx);
      return { ...f, bills, amount: applyBillsTotal(bills) };
    });

  const normDate = (s) => {
    s = String(s || "").trim();
    if (!s) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
    if (m) {
      let [, d, mo, y] = m;
      if (y.length === 2) y = "20" + y;
      return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return "";
  };
  const num = (v) => Number(String(v || "").replace(/[^0-9.-]/g, "")) || 0;
  const pasteRows = async () => {
    let text = "";
    try { text = await navigator.clipboard.readText(); } catch (e) { /* ignore */ }
    if (!text) text = window.prompt("Paste rows from Excel — columns: Branch, Bill No., Bill Date, LR No., LR Date, From, Destination, Amount, TDS, Deduction") || "";
    if (!text.trim()) return;
    const newRows = text.trim().split(/\r?\n/).map((line) => {
      const c = line.split("\t");
      return {
        branch: (c[0] || form.office || "Chennai").trim() || "Chennai",
        bill_no: (c[1] || "").trim(),
        bill_date: normDate(c[2]),
        cn_no: (c[3] || "").trim(),
        cn_date: normDate(c[4]),
        from_place: (c[5] || "").trim(),
        destination: (c[6] || "").trim(),
        amount: num(c[7]),
        tds: num(c[8]),
        deduction: num(c[9]),
      };
    });
    setForm((f) => {
      const bills = [...(f.bills || []), ...newRows];
      return { ...f, bills, amount: applyBillsTotal(bills) };
    });
    toast.success(`${newRows.length} row(s) pasted`);
  };

  const buildTdsGroups = () => {
    const byCust = {};
    displayRows.forEach((r) => {
      const push = (customer, row) => ((byCust[customer || "—"] = byCust[customer || "—"] || []).push(row));
      const mrNo = String(r.mr_no || "").replace(/^MR[-\s]?/i, "");
      if ((r.bills || []).length) {
        r.bills.forEach((b) => {
          if ((Number(b.tds) || 0) > 0) {
            push(r.customer_name, {
              mr_no: mrNo,
              date: fmtDate(r.date),
              bill_no: b.bill_no || "—",
              amount: Number(b.amount) || 0,
              tds: Number(b.tds) || 0,
              received: (Number(b.amount) || 0) - (Number(b.tds) || 0) - (Number(b.deduction) || 0),
            });
          }
        });
      } else if ((Number(r.tds) || 0) > 0) {
        const tds = Number(r.tds) || 0;
        const ded = Number(r.deduction) || 0;
        const gross = CHARGE_KEYS.reduce((s, k) => s + (Number(r[k]) || 0), 0);
        const amount = gross > 0 ? gross : Number(r.amount) || 0;
        push(r.customer_name, {
          mr_no: mrNo,
          date: fmtDate(r.date),
          bill_no: String(r.against_invoice_no || "").replace(/^INV[-\s]?/i, "") || "—",
          amount,
          tds,
          received: amount - tds - ded,
        });
      }
    });
    const groups = Object.keys(byCust)
      .sort((a, b) => a.localeCompare(b))
      .map((customer) => ({
        customer,
        rows: byCust[customer],
        amount: byCust[customer].reduce((s, x) => s + x.amount, 0),
        tds: byCust[customer].reduce((s, x) => s + x.tds, 0),
        received: byCust[customer].reduce((s, x) => s + x.received, 0),
      }));
    return groups;
  };

  const tdsPeriod = () =>
    filterFrom || filterTo ? `${filterFrom ? fmtDate(filterFrom) : "…"} to ${filterTo ? fmtDate(filterTo) : "…"}` : "All dates";

  const [chequeOpen, setChequeOpen] = useState(false);
  const [chequeTab, setChequeTab] = useState("received");
  const [issuedVouchers, setIssuedVouchers] = useState([]);
  const [issuedChallanPays, setIssuedChallanPays] = useState([]);
  useEffect(() => {
    if (!chequeOpen) return;
    api.get("/vouchers").then((r) => setIssuedVouchers((r.data || []).filter((v) => v.payment_mode === "Cheque"))).catch(() => {});
    api.get("/hire-challans").then((r) => {
      const list = [];
      (r.data || []).forEach((c) =>
        (c.payments || []).forEach((p) => {
          if (p.mode === "Cheque" || p.cheque_no)
            list.push({ ...p, challan_id: c.id, challan_no: c.challan_no, vehicle_no: c.vehicle_no, party_name: c.owner_name || c.vendor_broker_name || "" });
        })
      );
      setIssuedChallanPays(list);
    }).catch(() => {});
  }, [chequeOpen]);
  const issuedRows = [
    ...issuedVouchers.map((v) => ({ ...v, _src: "Voucher", _ref: String(v.voucher_no || "").replace(/^DV[-\s]?/i, "") })),
    ...issuedChallanPays.map((p) => ({ ...p, _src: "Challan", _ref: String(p.challan_no || "").replace(/^VHC[-\s]?/i, "") })),
  ].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  const [depositDate, setDepositDate] = useState(today());
  const chequeRows = displayRows.filter((r) => r.payment_mode === "Cheque" || r.cheque_no);
  const bounceCountByCust = (rows || []).reduce((m, r) => {
    if (!r.reversed && r.cheque_status === "Bounced" && r.customer_name) m[r.customer_name] = (m[r.customer_name] || 0) + 1;
    return m;
  }, {});
  const clearingDays = (r) => {
    if (!r.deposit_date) return null;
    const end = r.cleared_date || (r.cheque_status === "Cleared" ? null : today());
    if (!end) return null;
    const d = Math.round((new Date(end) - new Date(r.deposit_date)) / 86400000);
    return d >= 0 ? { days: d, pending: r.cheque_status !== "Cleared" } : null;
  };
  const [chequeMax, setChequeMax] = useState(false);
  const [chequeEdits, setChequeEdits] = useState({});
  const [bankDelayOpen, setBankDelayOpen] = useState(false);
  const [neftOpen, setNeftOpen] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(false);
  const paymentScores = () => {
    const m = {};
    (rows || []).forEach((r) => {
      if (r.reversed || !r.customer_name) return;
      const g = m[r.customer_name] || (m[r.customer_name] = { customer: r.customer_name, receipts: 0, bounces: 0, delays: [] });
      g.receipts += 1;
      if (r.cheque_status === "Bounced") g.bounces += 1;
      const b = billInfo(r);
      if (b.date && r.date) {
        const d = Math.round((new Date(r.date) - new Date(b.date)) / 86400000);
        if (d >= 0 && d < 1000) g.delays.push(d);
      }
    });
    return Object.values(m)
      .map((g) => {
        const avgDelay = g.delays.length ? Math.round(g.delays.reduce((a, v) => a + v, 0) / g.delays.length) : null;
        let score = "A";
        let reason = "No bounces, pays within 30 days";
        if (g.bounces >= 2 || (avgDelay != null && avgDelay > 60)) {
          score = "C";
          reason = g.bounces >= 2 ? `${g.bounces} bounced cheques` : `Avg. delay ${avgDelay} days`;
        } else if (g.bounces === 1 || (avgDelay != null && avgDelay > 30)) {
          score = "B";
          reason = g.bounces === 1 ? "1 bounced cheque" : `Avg. delay ${avgDelay} days`;
        } else if (avgDelay == null) {
          reason = "No bounces (delay data unavailable)";
        }
        return { ...g, avgDelay, score, reason };
      })
      .sort((a, b) => (a.score === b.score ? (b.avgDelay || 0) - (a.avgDelay || 0) : a.score.localeCompare(b.score)));
  };
  const bankDelayStats = () => {
    const m = {};
    chequeRows.forEach((r) => {
      if (!r.deposit_date) return;
      const bank = (r.bank_name || "").trim() || "Unknown Bank";
      const g = m[bank] || (m[bank] = { bank, total: 0, cleared: 0, days: 0, min: null, max: null, pending: 0 });
      g.total += 1;
      if (r.cheque_status === "Cleared" && r.cleared_date) {
        const d = Math.round((new Date(r.cleared_date) - new Date(r.deposit_date)) / 86400000);
        if (d >= 0) {
          g.cleared += 1;
          g.days += d;
          g.min = g.min == null ? d : Math.min(g.min, d);
          g.max = g.max == null ? d : Math.max(g.max, d);
        }
      } else if (r.cheque_status !== "Bounced") g.pending += 1;
    });
    return Object.values(m)
      .map((g) => ({ ...g, avg: g.cleared ? g.days / g.cleared : null }))
      .sort((a, b) => (a.avg ?? 9999) - (b.avg ?? 9999));
  };
  const neftListRows = () => {
    const m = {};
    (rows || []).forEach((r) => {
      if (r.reversed || r.cheque_status !== "Bounced" || !r.customer_name) return;
      const g = m[r.customer_name] || (m[r.customer_name] = { customer: r.customer_name, customer_id: r.customer_id, bounces: 0, amount: 0, banks: [], last: "" });
      g.bounces += 1;
      g.amount += Number(r.cheque_amount) || Number(r.amount) || 0;
      if (r.bank_name && !g.banks.includes(r.bank_name)) g.banks.push(r.bank_name);
      const d = r.deposit_date || r.cheque_date || r.date || "";
      if (d > g.last) g.last = d;
    });
    return Object.values(m)
      .filter((g) => g.bounces >= 2)
      .map((g) => {
        const c = customers.find((x) => x.id === g.customer_id) || customers.find((x) => (x.name || "").trim().toLowerCase() === g.customer.trim().toLowerCase());
        return { ...g, banks: g.banks.join(", "), phone: c?.phone || "" };
      })
      .sort((a, b) => b.bounces - a.bounces);
  };
  const saveChequeRow = async (r) => {
    const p = chequeEdits[r.id] || {};
    const changed = Object.entries(p).filter(([k, v]) => v !== (r[k] || ""));
    if (!changed.length) return toast.info("No changes to save");
    for (const [, v] of changed) {
      if (v && v.slice(0, 4) < "1950") return toast.error("Please enter a valid date (full year)");
    }
    for (const [k, v] of changed) await setChequeField(r, k, v);
    setChequeEdits((m) => {
      const n = { ...m };
      delete n[r.id];
      return n;
    });
  };
  const chequeRowsEnriched = chequeRows.map((r) => {
    const cd = clearingDays(r);
    return { ...r, _days: cd ? `${cd.days}d${cd.pending ? " (pending)" : ""}` : "", _repeat: (bounceCountByCust[r.customer_name] || 0) >= 2 };
  });
  const printSlip = () => {
    const slipRows = chequeRows.filter((r) => r.deposit_date === depositDate);
    if (!slipRows.length) {
      toast.error(`No cheques marked with deposit date ${fmtDate(depositDate)} — set Deposit Date on the rows first`);
      return;
    }
    printDepositSlip(slipRows, depositDate);
  };
  const reverseReceipt = async (r, undo = false) => {
    if (!undo && !window.confirm(`Reverse MR ${String(r.mr_no || "").replace(/^MR[-\s]?/i, "")}? The bill will go back to Unpaid/Partly Paid.`)) return;
    try {
      await api.post(`/receipts/${r.id}/reverse`, { undo });
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, reversed: !undo, ...(undo ? {} : { reversed_by: user?.name || "", reversed_at: new Date().toISOString() }) } : x)));
      toast.success(undo ? "Reversal undone — payment counted again" : "Receipt reversed — bill is back to unpaid");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setVoucherChequeStatus = async (v, status) => {
    try {
      await api.put(`/vouchers/${v.id}/cheque-status`, { status });
      setIssuedVouchers((prev) => prev.map((x) => (x.id === v.id ? { ...x, cheque_status: status } : x)));
      toast.success(`Cheque marked ${status}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setChallanPayChequeStatus = async (p, status) => {
    try {
      await api.put(`/hire-challans/${p.challan_id}/payments/${p.id}/cheque-status`, { status });
      setIssuedChallanPays((prev) => prev.map((x) => (x.id === p.id ? { ...x, cheque_status: status } : x)));
      toast.success(`Cheque marked ${status}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const issuedAsRegisterRows = () =>
    issuedRows.map((r) => ({
      ...r,
      mr_no: `${r._src === "Challan" ? "CH " : "DV "}${r._ref}${r._src === "Challan" && r.vehicle_no ? " · " + r.vehicle_no : ""}`,
      customer_name: r.party_name,
    }));

  const ISSUED_OPTS = { title: "CHEQUE ISSUED REGISTER (VOUCHERS + CHALLANS)", refLabel: "Ref No.", refPrefix: /^$/, partyLabel: "Paid To", showDeposit: false, showBounce: false };
  const downloadChequeCsv = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const amt = (r) => Number(r.cheque_amount) || Number(r.amount) || 0;
    const lines = [];
    if (chequeTab === "issued") {
      lines.push(["Source", "Ref No.", "Vehicle No.", "Date", "Paid To", "Cheque No.", "Cheque Date", "Bank Name", "Amount", "Clearing Status"].join(","));
      issuedRows.forEach((v) =>
        lines.push([v._src, v._ref, v.vehicle_no || "", v.date || "", v.party_name || "", v.cheque_no || "", v.cheque_date || "", v.bank_name || "", amt(v), v.cheque_status || "Pending"].map(cell).join(","))
      );
      lines.push(["TOTAL", "", "", "", "", "", "", "", issuedRows.reduce((a, v) => a + amt(v), 0), ""].map(cell).join(","));
    } else {
      lines.push(["MR No.", "MR Date", "Customer", "Bill No.", "Bill Date", "Bill Amount", "Cheque No.", "Cheque Date", "Bank Name", "Amount", "Deposit Date", "Cleared Date", "Clearing Days", "Clearing Status", "Bounce Charge", "Repeat Bouncer"].join(","));
      chequeRowsEnriched.forEach((r) => {
        const b = billInfo(r);
        lines.push([String(r.mr_no || "").replace(/^MR[-\s]?/i, ""), r.date || "", r.customer_name || "", b.no === "—" ? "" : b.no, b.date || "", b.amount || "", r.cheque_no || "", r.cheque_date || "", r.bank_name || "", amt(r), r.deposit_date || "", r.cleared_date || "", r._days || "", r.cheque_status || "Pending", Number(r.bounce_charge) || 0, r._repeat ? "YES" : ""].map(cell).join(","));
      });
      lines.push(["TOTAL", "", "", "", "", "", "", "", "", chequeRows.reduce((a, r) => a + amt(r), 0), "", "", "", "", chequeRows.reduce((a, r) => a + (Number(r.bounce_charge) || 0), 0), ""].map(cell).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cheque-register-${chequeTab}-${today()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const bounceWa = (r) => {
    const charge = Number(r.bounce_charge) || 0;
    const cust = customers.find((c) => c.id === r.customer_id) || customers.find((c) => (c.name || "").trim().toLowerCase() === (r.customer_name || "").trim().toLowerCase());
    const ph = String(cust?.phone || "").replace(/\D/g, "");
    const msg = [
      "⚠ *NTC — Cheque Bounce Notice*",
      `Dear ${r.customer_name || "Customer"},`,
      "",
      `Your cheque no. ${r.cheque_no || "—"}${r.bank_name ? ` (${r.bank_name})` : ""} of ${inr(Number(r.cheque_amount) || Number(r.amount) || 0)} against ${r.mr_no || "our receipt"} was returned unpaid by the bank.`,
      charge > 0 ? `Bank bounce charges of ${inr(charge)} have been added to your dues.` : "Any bank bounce charges will be added to your dues.",
      "",
      "Kindly arrange the payment / replacement cheque at the earliest.",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(`https://wa.me/${ph ? `91${ph.slice(-10)}` : ""}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const setChequeStatus = async (r, status) => {
    try {
      const payload = { status };
      if (status === "Cleared" && !r.cleared_date) payload.cleared_date = today();
      if (status !== "Cleared" && r.cleared_date) payload.cleared_date = "";
      await api.put(`/receipts/${r.id}/cheque-status`, payload);
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, cheque_status: status, ...("cleared_date" in payload ? { cleared_date: payload.cleared_date } : {}) } : x)));
      toast.success(`Cheque marked ${status}`);
      if (status === "Bounced") {
        toast.info("Opening WhatsApp bounce notice for the customer…");
        bounceWa({ ...r, cheque_status: status });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setChequeField = async (r, field, value) => {
    try {
      await api.put(`/receipts/${r.id}/cheque-status`, { [field]: value });
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, [field]: value } : x)));
      toast.success(field === "deposit_date" ? "Deposit date saved" : field === "cleared_date" ? "Cleared date saved" : "Bounce charge saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const printTds = () => {
    const groups = buildTdsGroups();
    if (!groups.length) {
      toast.info("No TDS deductions found for the selected period");
      return;
    }
    printTdsRegister(groups, tdsPeriod());
  };

  const downloadTdsCsv = () => {
    const groups = buildTdsGroups();
    if (!groups.length) {
      toast.info("No TDS deductions found for the selected period");
      return;
    }
    const csvCell = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [["Customer", "MR No.", "MR Date", "Bill No.", "Bill Amount", "TDS Deducted", "Received Amount"].join(",")];
    let ga = 0, gt = 0, gr = 0;
    groups.forEach((g) => {
      g.rows.forEach((x) => lines.push([g.customer, x.mr_no, x.date, x.bill_no, x.amount, x.tds, x.received].map(csvCell).join(",")));
      lines.push([`${g.customer} SUB TOTAL`, "", "", "", g.amount, g.tds, g.received].map(csvCell).join(","));
      ga += g.amount; gt += g.tds; gr += g.received;
    });
    lines.push(["GRAND TOTAL", "", "", "", ga, gt, gr].map(csvCell).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tds-register-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const save = async () => {
    if (!form.customer_id && !(mode === "single" && form.against_invoice_id)) {
      toast.error("Select a customer or an invoice");
      return;
    }
    if (mode === "multi" && !(form.bills || []).length) {
      toast.error("Add at least one bill row");
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const totalDed = (Number(form.deduction) || 0) + (form.bills || []).reduce((s, b) => s + (Number(b.deduction) || 0), 0);
    if (totalDed > 0 && !(form.deduction_reason || "").trim()) {
      toast.error("Enter the deduction reason — required for admin approval");
      return;
    }
    setSaving(true);
    let payload = mode === "multi" ? { ...form, against_invoice_id: "", against_invoice_no: "" } : { ...form, bills: [] };
    payload.customer_code = custCode || "";
    if (mode === "single" && payload.against_invoice_id && !payload.customer_id) {
      const inv = invoices.find((x) => x.id === payload.against_invoice_id);
      payload = {
        ...payload,
        customer_id: inv?.third_party_name ? "" : inv?.customer_id || "",
        customer_name: inv?.third_party_name || inv?.customer_name || "",
      };
    }
    try {
      let saved;
      if (editing) {
        const res = await api.put(`/receipts/${editing.id}`, payload);
        saved = res.data;
        toast.success(`Receipt ${saved.mr_no || ""} updated`, {
          description: "Do you want to print it now?",
          duration: 8000,
          action: { label: "Print", onClick: () => setPrintTarget(saved) },
        });
      } else {
        const res = await api.post("/receipts", payload);
        saved = res.data;
        toast.success(`Receipt ${saved.mr_no || ""} recorded`, {
          description: "Do you want to print it now?",
          duration: 8000,
          action: { label: "Print", onClick: () => setPrintTarget(saved) },
        });
        {
          const custRec =
            customers.find((c) => c.id === saved.customer_id) ||
            customers.find((c) => (c.name || "").trim().toLowerCase() === (saved.customer_name || "").trim().toLowerCase());
          const wdigits = String(custRec?.phone || "").replace(/\D/g, "");
          if (wdigits) {
            const thanks = `Dear ${saved.customer_name},\n\nReceived with thanks ₹${(Number(saved.amount) || 0).toLocaleString("en-IN")} by ${saved.payment_mode || "Cash"}${saved.payment_date ? ` dated ${fmtDate(saved.payment_date)}` : ""}${String(saved.against_invoice_no || "").trim() ? ` against Bill No. ${String(saved.against_invoice_no).replace(/^INV[-\s]?/i, "")}` : ""} (MR No. ${String(saved.mr_no || "").replace(/^MR[-\s]?/i, "") || "—"}).\n\nThank you for your prompt payment.\n\n— Nisha Transport Corporation`;
            toast.success(`Send "received with thanks" to ${saved.customer_name} on WhatsApp?`, {
              duration: 12000,
              action: {
                label: "WhatsApp",
                onClick: () => window.open(`https://wa.me/${wdigits.length === 10 ? "91" + wdigits : wdigits}?text=${encodeURIComponent(thanks)}`, "_blank"),
              },
            });
          }
        }
        if (saved.deduction_status === "Pending Approval") {
          toast.info("Deduction MR sent to admin for approval", { duration: 8000 });
          const dedAmt = (Number(saved.deduction) || 0) + (saved.bills || []).reduce((s, b) => s + (Number(b.deduction) || 0), 0);
          const msg = [
            "*NTC — Deduction MR Approval Request*",
            "",
            `MR No: ${String(saved.mr_no || "").replace(/^MR[-\s]?/i, "")}`,
            `Branch: ${saved.office || "—"}`,
            `Customer: ${saved.customer_name || "—"}`,
            `Received: ₹${(Number(saved.amount) || 0).toLocaleString("en-IN")}`,
            `Deduction: ₹${dedAmt.toLocaleString("en-IN")}`,
            `Reason: ${saved.deduction_reason || "—"}`,
            "",
            "Please approve or reject this deduction on the Dashboard → Deduction Approvals.",
          ].join("\n");
          window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
          toast.info("Admin notified on WhatsApp for deduction approval");
        }
      }
      setOpen(false);
      clearDraft();
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this receipt?")) return;
    try {
      await api.delete(`/receipts/${row.id}`);
      toast.success("Receipt deleted");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const dedOf = (r) => (Number(r.deduction) || 0) + (r.bills || []).reduce((s, b) => s + (Number(b.deduction) || 0), 0);
  const dedRegRows = (rows || [])
    .filter((r) => !r.reversed && dedOf(r) > 0 && String(r.date || "").startsWith(dedRegMonth))
    .map((r) => ({ ...r, _ded: dedOf(r) }))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const dedMonthLabel = dedRegMonth ? new Date(`${dedRegMonth}-01`).toLocaleString("en-IN", { month: "long", year: "numeric" }) : "";
  const DED_CLR = { Approved: "border-emerald-200 bg-emerald-50 text-emerald-700", Rejected: "border-rose-200 bg-rose-50 text-rose-700", "Pending Approval": "border-amber-200 bg-amber-50 text-amber-700" };
  const trendMonths = (() => {
    const end = new Date(`${dedRegMonth}-01`);
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(end.getFullYear(), end.getMonth() - (5 - i), 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
  })();
  const trendRows = (() => {
    const by = {};
    (rows || []).forEach((r) => {
      const m = String(r.date || "").slice(0, 7);
      if (r.reversed || !trendMonths.includes(m)) return;
      const cust = r.customer_name || "—";
      const a = (by[cust] = by[cust] || { customer: cust, received: 0, deducted: 0, months: {} });
      a.received += Number(r.amount) || 0;
      if ((r.deduction_status || "") !== "Rejected") {
        const d = dedOf(r);
        if (d > 0) {
          a.deducted += d;
          a.months[m] = (a.months[m] || 0) + d;
        }
      }
    });
    return Object.values(by)
      .filter((a) => a.deducted > 0)
      .map((a) => ({ ...a, pct: a.received + a.deducted > 0 ? (a.deducted / (a.received + a.deducted)) * 100 : 0 }))
      .sort((a, b) => b.pct - a.pct || b.deducted - a.deducted);
  })();
  const monShort = (m) => new Date(`${m}-01`).toLocaleString("en-IN", { month: "short", year: "2-digit" });

  return (
    <div>
      <MrCopyChoiceDialog target={printTarget} onClose={() => setPrintTarget(null)} />
      <Dialog open={!!replTarget} onOpenChange={(v) => !v && setReplTarget(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="replacement-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Replacement Cheque — MR {String(replTarget?.mr_no || "").replace(/^MR[-\s]?/i, "")}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Bounced cheque {replTarget?.cheque_no || "—"} of {replTarget?.customer_name || "—"}. Record the replacement cheque and track it until it clears.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-xs">Cheque No.</Label>
              <Input value={replForm.cheque_no} onChange={(e) => setReplForm((f) => ({ ...f, cheque_no: e.target.value }))} data-testid="replacement-cheque-no" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Cheque Date</Label>
              <Input type="date" value={replForm.cheque_date} onChange={(e) => setReplForm((f) => ({ ...f, cheque_date: e.target.value }))} data-testid="replacement-cheque-date" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Cheque Amount (₹)</Label>
              <Input type="number" value={replForm.cheque_amount} onChange={(e) => setReplForm((f) => ({ ...f, cheque_amount: e.target.value }))} data-testid="replacement-cheque-amount" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Bank Name</Label>
              <Input value={replForm.bank_name} onChange={(e) => setReplForm((f) => ({ ...f, bank_name: e.target.value }))} data-testid="replacement-bank-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplTarget(null)}>Cancel</Button>
            <Button onClick={saveReplacement} data-testid="save-replacement-btn">Save Replacement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={tdsYrOpen} onOpenChange={setTdsYrOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[860px]" data-testid="tds-yearly-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{yrLabel} Yearly Summary — {tdsYrMetric === "tds" ? "Form 26AS Matching" : `Customer-wise ${yrIsCharge ? "Charges" : "Deductions"}`}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex rounded-md border" data-testid="yearly-metric-toggle">
              {Object.entries(YR_METRICS).map(([k, lbl], i, arr) => (
                <Button
                  key={k}
                  variant={tdsYrMetric === k ? "default" : "ghost"}
                  size="sm"
                  className={i === 0 ? "rounded-r-none" : i === arr.length - 1 ? "rounded-l-none" : "rounded-none"}
                  onClick={() => setTdsYrMetric(k)}
                  data-testid={`yearly-metric-${k}`}
                >
                  {lbl.replace(" Charge", "")}
                </Button>
              ))}
            </div>
            <div className="grid gap-1.5">
              <Label>Financial Year</Label>
              <Select value={tdsYrFy} onValueChange={setTdsYrFy}>
                <SelectTrigger className="w-40" data-testid="tds-yearly-fy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FY_OPTIONS.map((y) => (
                    <SelectItem key={y} value={String(y)}>FY {fyLabel(y)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="pb-2 text-xs text-muted-foreground" data-testid="tds-yearly-note">
              {tdsYrMetric === "tds"
                ? "Quarterly TDS totals per customer — match each quarter with Form 26AS / AIS."
                : yrIsCharge
                ? `Quarterly ${yrLabel.toLowerCase()} totals per customer (from bills) for the financial year.`
                : "Quarterly deduction totals per customer (approved / not rejected) for the financial year."}
            </p>
            <div className="ml-auto flex gap-2 pb-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!tdsYearlyGroups.length) return toast.info(`No ${yrLabel} entries in this financial year`);
                  printTdsYearlySummary(tdsYearlyGroups, fyLabel(tdsYrFy), yrLabel, yrIsCharge ? yrBranchTotals : null);
                }}
                data-testid="tds-yearly-print-btn"
              >
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!tdsYearlyGroups.length) return toast.info(`No ${yrLabel} entries in this financial year`);
                  const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                  const csv = [
                    ["Customer", "Q1 (Apr-Jun)", "Q2 (Jul-Sep)", "Q3 (Oct-Dec)", "Q4 (Jan-Mar)", `Total ${yrLabel}`, "Bill Amount"].join(","),
                    ...tdsYearlyGroups.map((g) => [g.customer, ...g.q, g.tds, g.amount].map(q).join(",")),
                    ["GRAND TOTAL", ...[0, 1, 2, 3].map((i) => tdsYearlyGroups.reduce((s, g) => s + g.q[i], 0)), tdsYearlyGroups.reduce((s, g) => s + g.tds, 0), tdsYearlyGroups.reduce((s, g) => s + g.amount, 0)].map(q).join(","),
                    ...(yrIsCharge && yrBranchTotals.length
                      ? ["", `Branch-wise ${yrLabel}`, ...yrBranchTotals.map(([br, v]) => [br, "", "", "", "", v, ""].map(q).join(","))]
                      : []),
                  ].join("\n");
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
                  a.download = `${yrLabel.toLowerCase().replace(/\s+/g, "-")}-yearly-fy${fyLabel(tdsYrFy)}.csv`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
                data-testid="tds-yearly-export-btn"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-emerald-600 hover:text-emerald-700"
                onClick={() => {
                  if (!tdsYearlyGroups.length) return toast.info(`No ${yrLabel} entries in this financial year`);
                  const qt = (i) => tdsYearlyGroups.reduce((s, g) => s + g.q[i], 0);
                  const grand = tdsYearlyGroups.reduce((s, g) => s + g.tds, 0);
                  const msg = [
                    `*NTC — ${yrLabel} Yearly Summary (FY ${fyLabel(tdsYrFy)})*`,
                    `Customers: ${tdsYearlyGroups.length} · Total ${yrLabel}: ${inr(grand)}`,
                    "",
                    "*Quarter-wise:*",
                    `• Q1 (Apr–Jun): ${inr(qt(0))}`,
                    `• Q2 (Jul–Sep): ${inr(qt(1))}`,
                    `• Q3 (Oct–Dec): ${inr(qt(2))}`,
                    `• Q4 (Jan–Mar): ${inr(qt(3))}`,
                    "",
                    "*Customer-wise:*",
                    ...tdsYearlyGroups.slice(0, 15).map((g) => `• ${g.customer}: ${inr(g.tds)}`),
                    ...(tdsYearlyGroups.length > 15 ? [`… and ${tdsYearlyGroups.length - 15} more (see printed report)`] : []),
                    "",
                    ...(tdsYrMetric === "tds" ? ["For matching with Form 26AS / AIS.", ""] : []),
                    "— Nisha Transport Corporation",
                  ].join("\n");
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
                }}
                data-testid="tds-yearly-whatsapp-btn"
              >
                <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
              </Button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-auto rounded border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted">
                <tr className="text-left">
                  <th className="p-2">Customer</th>
                  <th className="p-2 text-right">Q1 (Apr–Jun)</th>
                  <th className="p-2 text-right">Q2 (Jul–Sep)</th>
                  <th className="p-2 text-right">Q3 (Oct–Dec)</th>
                  <th className="p-2 text-right">Q4 (Jan–Mar)</th>
                  <th className="p-2 text-right">Total {yrLabel}</th>
                  <th className="p-2 text-right">Bill Amount</th>
                </tr>
              </thead>
              <tbody>
                {tdsYearlyGroups.map((g) => (
                  <tr key={g.customer} className="border-t" data-testid="tds-yearly-row">
                    <td className="p-2 font-semibold">{g.customer}</td>
                    {g.q.map((v, i) => (
                      <td key={i} className="p-2 text-right tabular-nums">{v ? inr(v) : "—"}</td>
                    ))}
                    <td className="p-2 text-right font-bold tabular-nums text-amber-700">{inr(g.tds)}</td>
                    <td className="p-2 text-right tabular-nums">{inr(g.amount)}</td>
                  </tr>
                ))}
                {tdsYearlyGroups.length === 0 && (
                  <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No {yrLabel} entries in FY {fyLabel(tdsYrFy)}.</td></tr>
                )}
                {tdsYearlyGroups.length > 0 && (
                  <tr className="border-t bg-slate-100 font-bold" data-testid="tds-yearly-total-row">
                    <td className="p-2">GRAND TOTAL</td>
                    {[0, 1, 2, 3].map((i) => (
                      <td key={i} className="p-2 text-right tabular-nums">{inr(tdsYearlyGroups.reduce((s, g) => s + g.q[i], 0))}</td>
                    ))}
                    <td className="p-2 text-right tabular-nums text-amber-700">{inr(tdsYearlyGroups.reduce((s, g) => s + g.tds, 0))}</td>
                    <td className="p-2 text-right tabular-nums">{inr(tdsYearlyGroups.reduce((s, g) => s + g.amount, 0))}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {yrIsCharge && yrBranchTotals.length > 0 && (
            <div className="rounded border bg-slate-50 p-3" data-testid="yearly-branch-split">
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Branch-wise {yrLabel} — FY {fyLabel(tdsYrFy)}</div>
              <div className="flex flex-wrap gap-2">
                {yrBranchTotals.map(([br, v]) => (
                  <span key={br} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold" data-testid="yearly-branch-chip">
                    {br}: <span className="text-amber-700">{inr(v)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={recovOpen} onOpenChange={setRecovOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[900px]" data-testid="recovery-register-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ReceiptText className="h-5 w-5 text-rose-600" /> Cheque Recovery Register
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label className="text-xs">Month</Label>
              <Input type="month" className="h-9 w-[170px]" value={recovMonth} onChange={(e) => setRecovMonth(e.target.value)} data-testid="recovery-register-month" />
            </div>
            <p className="pb-2 text-xs text-muted-foreground">
              {recovRows.length} bounced cheque(s) in {recovMonthLabel} · Recovered {recovRows.filter((r) => r._recovery === "Recovered").length} · Open {recovRows.filter((r) => r._recovery !== "Recovered").length}
            </p>
          </div>
          {recovRows.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="recovery-register-empty">No bounced cheques in {recovMonthLabel}. 🎉</div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="recovery-register-table">
                <thead className="bg-rose-50">
                  <tr className="[&>th]:whitespace-nowrap [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-rose-900">
                    <th>MR No.</th><th>Date</th><th>Customer</th><th>Bounced Cheque</th><th className="text-right">Amount</th><th>Replacement</th><th>Recovery</th>
                  </tr>
                </thead>
                <tbody>
                  {recovRows.map((r) => (
                    <tr key={r.id} className="border-t [&>td]:px-2.5 [&>td]:py-2" data-testid="recovery-register-row">
                      <td className="font-medium">{String(r.mr_no || "—").replace(/^MR[-\s]?/i, "")}</td>
                      <td className="whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td>{r.customer_name || "—"}</td>
                      <td>{r.cheque_no || "—"}{r.bank_name ? ` · ${r.bank_name}` : ""}</td>
                      <td className="text-right tabular-nums">{inr(Number(r.cheque_amount) || Number(r.amount) || 0)}</td>
                      <td>{r._repl || "—"}</td>
                      <td>
                        <span className={`inline-block rounded-full border px-2 py-0.5 font-medium ${RECOV_CLR[r._recovery]}`} data-testid="recovery-status-chip">{r._recovery}</span>
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t bg-slate-50 font-semibold [&>td]:px-2.5 [&>td]:py-2">
                    <td colSpan={4} className="text-right">TOTAL BOUNCED</td>
                    <td className="text-right tabular-nums" data-testid="recovery-register-total">{inr(recovRows.reduce((s, r) => s + (Number(r.cheque_amount) || Number(r.amount) || 0), 0))}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                const lines = [
                  `Cheque Recovery Register — ${recovMonthLabel}`,
                  "MR No.,Date,Customer,Bounced Cheque,Bank,Amount,Replacement,Recovery Status",
                  ...recovRows.map((r) => [String(r.mr_no || "").replace(/^MR[-\s]?/i, ""), r.date, r.customer_name, r.cheque_no || "", r.bank_name || "", Number(r.cheque_amount) || Number(r.amount) || 0, r._repl, r._recovery].map(cell).join(",")),
                ].join("\n");
                const url = URL.createObjectURL(new Blob([lines], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `recovery-register-${recovMonth}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              disabled={!recovRows.length}
              data-testid="recovery-register-excel-btn"
            >
              <Download className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button variant="outline" onClick={() => printRecoveryRegister(recovRows, recovMonthLabel)} disabled={!recovRows.length} data-testid="recovery-register-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            <Button onClick={() => setRecovOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={dedApprOpen} onOpenChange={setDedApprOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Deduction Approvals — Pending ({pendingDed.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">Branch MRs with deductions wait here. Approve to accept the deduction (the branch MR then stands for the deduction payment), or Reject to disallow it.</div>
            {pendingDed.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="ded-appr-empty">No deductions waiting for approval.</div>
            ) : (
              pendingDed.map((r) => {
                const ded = (Number(r.deduction) || 0) + ((r.bills || []).reduce((s, b) => s + (Number(b.deduction) || 0), 0));
                return (
                  <div key={r.id} className="rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="ded-appr-row">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-bold">MR {String(r.mr_no || "").replace(/^MR[-\s]?/i, "") || "—"} · {r.customer_name || "—"} <span className="font-normal text-muted-foreground">({r.office || "Chennai"} · {fmtDate(r.date)})</span></div>
                        <div className="mt-0.5 text-xs text-muted-foreground">Received {inr2(r.amount)} · Deduction <b className="text-amber-700">{inr2(ded)}</b></div>
                        <div className="mt-0.5 text-xs"><b>Reason:</b> {r.deduction_reason || "—"}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="h-8 bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => reviewDed(r, true)} data-testid="ded-approve-btn">Approve</Button>
                        <Button size="sm" variant="outline" className="h-8 border-rose-300 text-rose-600 hover:bg-rose-50" onClick={() => reviewDed(r, false)} data-testid="ded-reject-btn">Reject</Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dedRegOpen} onOpenChange={setDedRegOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[960px]" data-testid="deduction-register-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ReceiptText className="h-5 w-5 text-amber-600" /> Deduction Register
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label className="text-xs">Month</Label>
              <Input type="month" className="h-9 w-[170px]" value={dedRegMonth} onChange={(e) => setDedRegMonth(e.target.value)} data-testid="deduction-register-month" />
            </div>
            <div className="flex overflow-hidden rounded-md border border-border">
              <button type="button" className={`px-3 py-1.5 text-xs font-semibold ${dedView === "register" ? "bg-amber-600 text-white" : "bg-white text-slate-600"}`} onClick={() => setDedView("register")} data-testid="ded-view-register">
                Register
              </button>
              <button type="button" className={`px-3 py-1.5 text-xs font-semibold ${dedView === "trend" ? "bg-amber-600 text-white" : "bg-white text-slate-600"}`} onClick={() => setDedView("trend")} data-testid="ded-view-trend">
                Trend (6 months)
              </button>
            </div>
            <p className="pb-2 text-xs text-muted-foreground">
              {dedView === "register"
                ? `${dedRegRows.length} deduction MR(s) · Total deducted ${inr(dedRegRows.reduce((s, r) => s + r._ded, 0))} in ${dedMonthLabel}`
                : `Highest-deducting customers, ${monShort(trendMonths[0])} → ${monShort(trendMonths[5])} — use before rate discussions`}
            </p>
          </div>
          {dedView === "trend" ? (
            trendRows.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="deduction-trend-empty">No deductions in the last 6 months.</div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs" data-testid="deduction-trend-table">
                  <thead className="bg-amber-50">
                    <tr className="[&>th]:whitespace-nowrap [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-amber-900">
                      <th>Customer</th>
                      {trendMonths.map((m) => <th key={m} className="text-right">{monShort(m)}</th>)}
                      <th className="text-right">Total Deducted</th>
                      <th className="text-right">Deduction %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trendRows.map((r) => (
                      <tr key={r.customer} className="border-t [&>td]:px-2.5 [&>td]:py-2" data-testid="deduction-trend-row">
                        <td className="font-medium">{r.customer}</td>
                        {trendMonths.map((m) => (
                          <td key={m} className="text-right tabular-nums">{r.months[m] ? inr(r.months[m]) : <span className="text-muted-foreground">—</span>}</td>
                        ))}
                        <td className="text-right font-semibold tabular-nums">{inr(r.deducted)}</td>
                        <td className="text-right">
                          <span className={`inline-block rounded-full px-2 py-0.5 font-bold ${r.pct >= 3 ? "bg-rose-50 text-rose-700" : r.pct >= 1 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="deduction-trend-pct">
                            {r.pct.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : dedRegRows.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="deduction-register-empty">No deductions in {dedMonthLabel}.</div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="deduction-register-table">
                <thead className="bg-amber-50">
                  <tr className="[&>th]:whitespace-nowrap [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-amber-900">
                    <th>MR No.</th><th>MR Date</th><th>Branch</th><th>Customer</th><th className="text-right">Received</th><th className="text-right">Deduction</th><th>Reason</th><th>Status</th><th>Reviewed By</th>
                  </tr>
                </thead>
                <tbody>
                  {dedRegRows.map((r) => (
                    <tr key={r.id} className="border-t [&>td]:px-2.5 [&>td]:py-2" data-testid="deduction-register-row">
                      <td className="font-medium">{String(r.mr_no || "—").replace(/^MR[-\s]?/i, "")}</td>
                      <td className="whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td>{r.office || "Chennai"}</td>
                      <td>{r.customer_name || "—"}</td>
                      <td className="text-right tabular-nums">{inr(r.amount)}</td>
                      <td className="text-right font-semibold tabular-nums">{inr(r._ded)}</td>
                      <td className="max-w-[200px]">{r.deduction_reason || "—"}</td>
                      <td>
                        <span className={`inline-block rounded-full border px-2 py-0.5 font-medium ${DED_CLR[r.deduction_status || "Approved"] || "border-slate-200 text-slate-600"}`}>
                          {r.deduction_status || "Approved"}
                        </span>
                      </td>
                      <td>{r.deduction_reviewed_by || "—"}</td>
                    </tr>
                  ))}
                  <tr className="border-t bg-slate-50 font-semibold [&>td]:px-2.5 [&>td]:py-2">
                    <td colSpan={4} className="text-right">TOTAL</td>
                    <td className="text-right tabular-nums">{inr(dedRegRows.reduce((s, r) => s + (Number(r.amount) || 0), 0))}</td>
                    <td className="text-right tabular-nums" data-testid="deduction-register-total">{inr(dedRegRows.reduce((s, r) => s + r._ded, 0))}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                const lines = dedView === "trend"
                  ? [
                      `Deduction Trend — ${monShort(trendMonths[0])} to ${monShort(trendMonths[5])}`,
                      ["Customer", ...trendMonths.map(monShort), "Total Deducted", "Deduction %"].join(","),
                      ...trendRows.map((r) => [r.customer, ...trendMonths.map((m) => r.months[m] || 0), r.deducted, `${r.pct.toFixed(1)}%`].map(cell).join(",")),
                    ].join("\n")
                  : [
                      `Deduction Register — ${dedMonthLabel}`,
                      "MR No.,MR Date,Branch,Customer,Received,Deduction,Reason,Status,Reviewed By",
                      ...dedRegRows.map((r) => [String(r.mr_no || "").replace(/^MR[-\s]?/i, ""), r.date, r.office || "Chennai", r.customer_name, r.amount, r._ded, r.deduction_reason || "", r.deduction_status || "Approved", r.deduction_reviewed_by || ""].map(cell).join(",")),
                    ].join("\n");
                const url = URL.createObjectURL(new Blob([lines], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = dedView === "trend" ? `deduction-trend-${dedRegMonth}.csv` : `deduction-register-${dedRegMonth}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              disabled={dedView === "trend" ? !trendRows.length : !dedRegRows.length}
              data-testid="deduction-register-excel-btn"
            >
              <Download className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => (dedView === "trend" ? printDeductionTrend(trendRows, trendMonths, `${monShort(trendMonths[0])} — ${monShort(trendMonths[5])}`) : printDeductionRegister(dedRegRows, dedMonthLabel))}
              disabled={dedView === "trend" ? !trendRows.length : !dedRegRows.length}
              data-testid="deduction-register-print-btn"
            >
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            <Button onClick={() => setDedRegOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BlankMrDialog open={blankMrOpen} onClose={() => setBlankMrOpen(false)} onPrint={(list) => setPrintTarget(list)} existingRows={rows || []} />
      <PageHeader
        title="Money Receipts (MR)"
        subtitle="Record payments received from customers. Linking to an invoice auto-updates its paid status."
        action={
          <Button data-testid="add-receipt-btn" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> New Receipt
          </Button>
        }
      />

      {(() => {
        const stale = (rows || []).filter((r) => {
          if (r.reversed || !r.deposit_date) return false;
          if (!(r.payment_mode === "Cheque" || r.cheque_no)) return false;
          if (["Cleared", "Bounced"].includes(r.cheque_status)) return false;
          return Math.floor((Date.now() - new Date(r.deposit_date).getTime()) / 86400000) > 5;
        });
        if (!stale.length) return null;
        return (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="cheque-reminder-banner">
            <div className="text-sm font-bold text-amber-800">⚠ {stale.length} cheque(s) deposited over 5 days ago and still not cleared — follow up with the bank</div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-amber-700">
              {stale.slice(0, 6).map((r) => (
                <span key={r.id} data-testid="cheque-reminder-item">
                  {r.customer_name} — Chq {r.cheque_no || "?"} ({Math.floor((Date.now() - new Date(r.deposit_date).getTime()) / 86400000)} days, dep. {fmtDate(r.deposit_date)})
                </span>
              ))}
              {stale.length > 6 && <span>+{stale.length - 6} more in Cheque Register</span>}
            </div>
          </div>
        );
      })()}

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-slate-50 px-4 py-3" data-testid="receipt-filter-bar">
        <div className="grid gap-1">
          <Label className="text-xs">Search Customer</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              className="h-9 w-[220px] bg-white pl-8 pr-7"
              placeholder="Name / code / MR no."
              value={custSearch}
              onChange={(e) => setCustSearch(e.target.value)}
              data-testid="receipt-customer-search"
            />
            {custSearch && (
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setCustSearch("")} data-testid="receipt-customer-search-clear">
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Date From</Label>
          <Input type="date" className="h-9 w-[150px] bg-white" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} data-testid="receipt-filter-from" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-9 w-[150px] bg-white" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} data-testid="receipt-filter-to" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Financial Year</Label>
          <Select
            value={fyPick}
            onValueChange={(v) => {
              setFyPick(v);
              if (v === "all") {
                setFilterFrom("");
                setFilterTo("");
              } else {
                const y = Number(v);
                setFilterFrom(`${y}-04-01`);
                setFilterTo(`${y + 1}-03-31`);
              }
            }}
          >
            <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="fy-filter">
              <SelectValue placeholder="FY (Apr–Mar)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All dates</SelectItem>
              {FY_OPTIONS.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  FY {y}-{String((y + 1) % 100).padStart(2, "0")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Jump to MR No.</Label>
          <div className="flex gap-1.5">
            <Input
              className="h-9 w-[130px] bg-white"
              placeholder="MR No."
              value={mrJump}
              onChange={(e) => setMrJump(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && jumpToMr()}
              data-testid="mr-jump-input"
            />
            <Button variant="outline" size="icon" className="h-9 w-9 bg-white" onClick={jumpToMr} title="Jump to MR" data-testid="mr-jump-btn">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {(filterFrom || filterTo) && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setFilterFrom(""); setFilterTo(""); }} data-testid="receipt-filter-clear">
            Clear
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-9 bg-white"
          onClick={() => printReceiptRegister((selectedRows.length ? selectedRows : displayRows).map((r) => ({ ...r, customer_code: rowCode(r) })), { from: filterFrom, to: filterTo })}
          disabled={!displayRows.length}
          data-testid="print-selected-receipts-btn"
        >
          <Printer className="mr-1.5 h-3.5 w-3.5" /> Print{sel.size ? ` (${selectedRows.length})` : ""}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9 bg-white"
          onClick={() => {
            const list = selectedRows.length ? selectedRows : displayRows;
            const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
            const lines = ["Sl No.,Branch,MR No.,MR Date,Received From (Customer Name),Code,Against Bill No.,Bill Date,Bill Amount,Mode of Payment,Received Amount,Payment Advice,Payment Date"];
            list.forEach((r, i) => { const bi = billInfo(r); lines.push([i + 1, r.office || "Chennai", String(r.mr_no || "").replace(/^MR[-\s]?/i, ""), r.date || "", r.customer_name || "", rowCode(r) || "", bi.no === "—" ? "" : bi.no, bi.date || "", bi.amount || "", r.payment_mode || "", r.amount || 0, r.advice_path ? "Yes" : "No", r.payment_date || ""].map(cell).join(",")); });
            lines.push(`,,,,,,,,,TOTAL,${list.reduce((s, r) => s + (Number(r.amount) || 0), 0)},,`);
            const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ntc-money-receipts-${today()}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          }}
          disabled={!displayRows.length}
          data-testid="receipts-excel-btn"
        >
          <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
        </Button>
        <Button size="sm" variant="outline" className="h-9 bg-white" onClick={sendDailyCollection} data-testid="daily-collection-wa-btn">
          <MessageCircle className="mr-1.5 h-3.5 w-3.5 text-emerald-600" /> Daily Collection
        </Button>
        {isAdmin && (
          <Button size="sm" variant="outline" className={`h-9 bg-white ${pendingDed.length ? "border-amber-400 text-amber-700" : ""}`} onClick={() => setDedApprOpen(true)} data-testid="deduction-approvals-btn">
            <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Deduction Approvals{pendingDed.length ? ` (${pendingDed.length})` : ""}
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-9 bg-white" onClick={printTds} data-testid="tds-register-btn">
            <Printer className="mr-1.5 h-3.5 w-3.5" /> TDS Register
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-9 bg-white" onClick={downloadTdsCsv} data-testid="tds-excel-btn">
            <Download className="mr-1.5 h-3.5 w-3.5" /> TDS Excel
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => { setTdsYrMetric("tds"); setTdsYrOpen(true); }} data-testid="tds-yearly-btn">
            <ReceiptText className="mr-1.5 h-3.5 w-3.5" /> TDS Yearly
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => { setTdsYrMetric("deduction"); setTdsYrOpen(true); }} data-testid="deduction-yearly-btn">
            <ReceiptText className="mr-1.5 h-3.5 w-3.5" /> Deduction Yearly
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => { setTdsYrMetric("loading"); setTdsYrOpen(true); }} data-testid="charges-yearly-btn">
            <ReceiptText className="mr-1.5 h-3.5 w-3.5" /> Charges Yearly
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => setRecovOpen(true)} data-testid="recovery-register-btn">
            <ReceiptText className="mr-1.5 h-3.5 w-3.5" /> Recovery Register
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => setDedRegOpen(true)} data-testid="deduction-register-btn">
            <ReceiptText className="mr-1.5 h-3.5 w-3.5" /> Deduction Register
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => setChequeOpen(true)} data-testid="cheque-register-btn">
            <ReceiptText className="mr-1.5 h-3.5 w-3.5" /> Cheque Register
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => setScoreOpen(true)} data-testid="payment-score-btn">
            <ReceiptText className="mr-1.5 h-3.5 w-3.5" /> Payment Score
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => setBlankMrOpen(true)} data-testid="blank-mr-print-btn">
          <Printer className="mr-1.5 h-3.5 w-3.5" /> Blank Print
        </Button>
        <Button
          size="sm"
          variant={partOnly ? "default" : "outline"}
          className={`h-9 ${partOnly ? "" : "bg-white"}`}
          onClick={() => setPartOnly((v) => !v)}
          data-testid="part-only-toggle"
        >
          Part Payments{partOnly ? " ✓" : ""}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9 bg-white"
          onClick={() => {
            const list = (rows || []).filter(isPartPaid).map((r) => { const bi = billInfo(r); return { ...r, _bill_no: bi.no, _bill_date: bi.date, _bill_amount: bi.amount }; });
            if (!list.length) { toast.info("No part-paid receipts found."); return; }
            printPartPaymentSummary(list);
          }}
          data-testid="part-summary-print-btn"
        >
          <Printer className="mr-1.5 h-3.5 w-3.5" /> Part Summary
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9 bg-white"
          onClick={() => {
            const list = (rows || []).filter(isPartPaid);
            if (!list.length) { toast.info("No part-paid receipts found."); return; }
            let tPend = 0;
            const lines = list
              .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")))
              .map((r, i) => {
                const bi = billInfo(r);
                const ded = (Number(r.tds) || 0) + (Number(r.deduction) || 0);
                const pend = (Number(bi.amount) || 0) - (Number(r.amount) || 0) - ded;
                tPend += pend;
                return `${i + 1}. ${r.customer_name || "—"} — Bill ${bi.no} (₹${(Number(bi.amount) || 0).toLocaleString("en-IN")}), Recd ₹${(Number(r.amount) || 0).toLocaleString("en-IN")}${ded ? ` + TDS/Ded ₹${ded.toLocaleString("en-IN")}` : ""}${r.date ? ` on ${fmtDate(r.date)}` : ""} — *Pending ₹${pend.toLocaleString("en-IN")}*`;
              });
            const msg = `*NISHA TRANSPORT CORPORATION*\n*PART PAYMENT PENDING LIST*\nAs on ${fmtDate(today())}\n\n${lines.join("\n")}\n\n*TOTAL PENDING: ₹${tPend.toLocaleString("en-IN")}* (${list.length} receipt${list.length === 1 ? "" : "s"})`;
            window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
          }}
          data-testid="part-summary-wa-btn"
        >
          <MessageCircle className="mr-1.5 h-3.5 w-3.5 text-emerald-600" /> Part WhatsApp
        </Button>
        <PadLogButton />
        <span className="pb-2 text-xs text-muted-foreground">{displayRows.length} receipt(s){sel.size ? ` · ${selectedRows.length} selected` : ""}</span>
      </div>

      {custSearch.trim() && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-2.5" data-testid="mr-search-total">
          <span className="text-sm font-bold text-emerald-800">
            Total Received — "{custSearch.trim()}": {inr2(displayRows.reduce((s, r) => s + (Number(r.amount) || 0), 0))}
          </span>
          <span className="text-xs text-emerald-700" data-testid="mr-search-total-count">{displayRows.length} receipt(s)</span>
        </div>
      )}
      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-[36px]">
                <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="receipt-select-all" />
              </TableHead>
              {["SL No.", "Branch", "MR No.", "MR Date", "Received From (Customer Name)", "Customer Code", "Against Bill No.", "Bill Date", "Bill Amount", "Mode of Payment", "Received Amount", "Payment Advice", "Payment Date"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  {h}
                </TableHead>
              ))}
              <TableHead className="w-[70px] text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 12 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && displayRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={15} className="py-12 text-center text-muted-foreground">
                  {rows.length === 0 ? "No receipts yet. Record your first payment received." : custSearch ? "No receipts match your search." : "No receipts in the selected date range."}
                </TableCell>
              </TableRow>
            )}
            {rows &&
              pagedReceipts.map((r, pi) => (
                <TableRow key={r.id} data-testid="receipts-row" className={mrHl === r.id ? "bg-amber-100 hover:bg-amber-100" : ""}>
                  <TableCell>
                    <Checkbox checked={sel.has(r.id)} onCheckedChange={() => toggleOne(r.id)} data-testid="receipt-select-row" />
                  </TableCell>
                  <TableCell className="text-sm" data-testid="receipt-sl-cell">{(curMrPage - 1) * 20 + pi + 1}</TableCell>
                  <TableCell className="text-sm">{r.office || "Chennai"}</TableCell>
                  <TableCell className="font-medium">
                    {String(r.mr_no || "").replace(/^MR[-\s]?/i, "") || "—"}
                    <NewBadge createdAt={r.created_at} />
                    {r.reversed && (
                      <span className="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700" data-testid="receipt-reversed-badge">Reversed</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm font-medium" data-testid="receipt-cust-code-cell">{rowCode(r) || "—"}</TableCell>
                  <TableCell className="text-sm">{billInfo(r).no}</TableCell>
                  <TableCell className="text-sm" data-testid="receipt-bill-date-cell">{billInfo(r).date ? fmtDate(billInfo(r).date) : "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums" data-testid="receipt-bill-amount-cell">{billInfo(r).amount ? inr2(billInfo(r).amount) : "—"}</TableCell>
                  <TableCell className="text-sm">{r.payment_mode}</TableCell>
                  <TableCell className="text-sm font-medium tabular-nums text-emerald-600">
                    {inr2(r.amount)}
                    {(() => { const ba = Number(billInfo(r).amount) || 0; const cov = (Number(r.amount) || 0) + (Number(r.tds) || 0) + (Number(r.deduction) || 0); return ba > 0 && cov < ba - 0.5 ? (
                      <>
                        <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" title={`Bill ${inr2(ba)} — short by ${inr2(ba - cov)}${Number(r.tds) || Number(r.deduction) ? ` (after TDS/deduction ${inr2((Number(r.tds) || 0) + (Number(r.deduction) || 0))})` : ""}`} data-testid="receipt-part-badge">Part</span>
                        <button
                          type="button"
                          className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded text-emerald-600 hover:bg-emerald-50"
                          title={`WhatsApp pending balance ${inr2(ba - cov)} to ${r.customer_name || "customer"}`}
                          onClick={(e) => { e.stopPropagation(); waPartBalance(r); }}
                          data-testid="receipt-part-wa-btn"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : null; })()}
                  </TableCell>
                  <TableCell>
                    <AdviceCell row={r} onDone={loadAll} />
                  </TableCell>
                  <TableCell className="text-sm" data-testid="receipt-payment-date-cell">{r.payment_date ? fmtDate(r.payment_date) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-primary"
                        title="Print receipt"
                        onClick={() => setPrintTarget({ ...r, customer_code: rowCode(r) })}
                        data-testid="print-receipt-btn"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Edit receipt"
                        onClick={() => openEdit(r)}
                        data-testid="edit-receipt-btn"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => remove(r)}
                        data-testid="delete-receipt-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableShell>
      <ListPagination total={displayRows.length} page={page} setPage={setPage} noun="receipts" testPrefix="receipts" />

      {open && dlgView === "min" && (
        <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 shadow-xl" data-testid="receipt-dialog-minbar">
          <ReceiptText className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{editing ? "Edit Money Receipt" : "New Money Receipt"}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" title="Restore" onClick={() => setDlgView("normal")} data-testid="receipt-dialog-restore">
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Close" onClick={() => { clearDraft(); setOpen(false); setDlgView("normal"); }} data-testid="receipt-dialog-discard">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <Dialog open={open && dlgView !== "min"} onOpenChange={(v) => { setOpen(v); if (!v) { setDlgView("normal"); clearDraft(); } }}>
        <DialogContent className={dlgView === "max" ? "h-[95vh] max-h-[95vh] w-[97vw] max-w-[97vw] overflow-y-auto" : `max-h-[90vh] overflow-y-auto ${mode === "multi" ? "sm:max-w-[960px]" : "sm:max-w-[540px]"}`}>
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="flex items-center gap-2 font-display">
                <ReceiptText className="h-4 w-4 text-primary" /> {editing ? "Edit Money Receipt" : "New Money Receipt"}
                {editing?.edit_log?.length ? (
                  <span className="text-[11px] font-normal text-muted-foreground" data-testid="mr-edit-history">
                    · Updated: {editing.edit_log.slice(-2).map((e) => `${e.by || "—"} on ${fmtDate(String(e.at || "").slice(0, 10))}`).join(" · ")}
                  </span>
                ) : null}
              </DialogTitle>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Minimize" onClick={() => { saveDraft(); setDlgView("min"); }} data-testid="receipt-dialog-minimize">
                  <Minus className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" title={dlgView === "max" ? "Restore size" : "Maximize"} onClick={() => setDlgView(dlgView === "max" ? "normal" : "max")} data-testid="receipt-dialog-maximize">
                  {dlgView === "max" ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={mode === "single" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setMode("single")} data-testid="receipt-mode-single">
                Single Receipt
              </Button>
              <Button type="button" size="sm" variant={mode === "multi" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setMode("multi")} data-testid="receipt-mode-multi">
                Multiple Bills
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>MR No. (optional)</Label>
                <Input value={form.mr_no || ""} onChange={(e) => setForm({ ...form, mr_no: e.target.value })} placeholder="Leave blank to auto-generate" data-testid="receipt-mr-no" />
              </div>
              <div className="grid gap-1.5">
                <Label>Branch</Label>
                <Select value={form.office || "Chennai"} onValueChange={(v) => setForm({ ...form, office: v })}>
                  <SelectTrigger data-testid="receipt-office"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BRANCHES.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Customer Code</Label>
              <Input
                value={custCode}
                placeholder="e.g. CUST-1001"
                data-testid="receipt-customer-code"
                onChange={(e) => {
                  const v = e.target.value;
                  setCustCode(v);
                  const key = v.trim().toLowerCase();
                  const c = key ? customers.find((x) => (x.customer_code || "").toLowerCase() === key) : null;
                  if (c) setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name, against_invoice_id: "", against_invoice_no: "" }));
                }}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Received from (customer)</Label>
              <Select
                value={form.customer_id}
                onValueChange={(v) => {
                  const c = customers.find((x) => x.id === v);
                  setForm({ ...form, customer_id: v, customer_name: c ? c.name : "", against_invoice_id: "", against_invoice_no: "" });
                  setCustCode(c?.customer_code || "");
                }}
              >
                <SelectTrigger data-testid="receipt-customer">
                  {form.customer_name ? <span className="truncate">{form.customer_name}</span> : <SelectValue placeholder="Select customer" />}
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {mode === "single" && (
            <div className="grid gap-1.5">
              <Label>Against invoice (optional)</Label>
              <Select
                value={form.against_invoice_id || "none"}
                onValueChange={(v) => {
                  if (v === "none") {
                    setForm({ ...form, against_invoice_id: "", against_invoice_no: "" });
                    return;
                  }
                  const inv = invoices.find((x) => x.id === v);
                  const bal = outstanding[v] && outstanding[v].status === "Partly Paid" ? outstanding[v].balance : 0;
                  const base = bal > 0 ? bal : inv ? inv.total : 0;
                  setForm(
                    applyAmount({
                      ...form,
                      against_invoice_id: v,
                      against_invoice_no: inv ? String(inv.invoice_no || "").replace(/^INV[-\s]?/i, "") : "",
                      freight_charge: base,
                      amount: base,
                      customer_id: "",
                      customer_name: "",
                    })
                  );
                  setCustCode("");
                }}
              >
                <SelectTrigger data-testid="receipt-invoice"><SelectValue placeholder="No specific invoice" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific invoice</SelectItem>
                  {custInvoices.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {String(i.invoice_no || "").replace(/^INV[-\s]?/i, "")} — {inr2(i.total)} ({i.status}
                      {i.status === "Partly Paid" && outstanding[i.id] ? ` · ${inr2(outstanding[i.id].balance)} pending` : ""})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            {mode === "single" && (
            <div className="grid grid-cols-3 gap-3">
              {CHARGE_KEYS.map((k) => (
                <div key={k} className="grid gap-1.5">
                  <Label className="text-xs">{CHARGE_LABELS[k]}</Label>
                  <Input
                    type="number"
                    data-testid={`receipt-${k}`}
                    value={form[k]}
                    onChange={(e) =>
                      setForm((f) => applyAmount({ ...f, [k]: Number(e.target.value) }))
                    }
                  />
                </div>
              ))}
            </div>
            )}

            {mode === "single" && (
            <div className="grid gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs">TDS:</Label>
                <Button type="button" size="sm" variant={form.tds_mode === "auto" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setForm((f) => applyAmount({ ...f, tds_mode: "auto" }))} data-testid="tds-mode-auto">
                  Auto (%)
                </Button>
                <Button type="button" size="sm" variant={form.tds_mode !== "auto" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setForm((f) => applyAmount({ ...f, tds_mode: "manual" }))} data-testid="tds-mode-manual">
                  Manual
                </Button>
                {form.tds_mode === "auto" && (
                  <Select value={String(form.tds_pct || 0)} onValueChange={(v) => setForm((f) => applyAmount({ ...f, tds_pct: Number(v) }))}>
                    <SelectTrigger className="h-7 w-[90px] text-xs" data-testid="tds-pct-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="1">1%</SelectItem>
                      <SelectItem value="2">2%</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <span className="text-[11px] text-muted-foreground">(on total of all charges)</span>
              </div>
              {(() => {
                const norm = (v) => String(v || "").replace(/^INV[-\s]?/i, "").replace(/^0+/, "").trim();
                const inv = invoices.find((i) => i.id === form.against_invoice_id) || invoices.find((i) => form.against_invoice_no && norm(i.invoice_no) === norm(form.against_invoice_no));
                const ba = Number(inv?.total) || 0;
                const gap = Math.round((ba - (Number(form.amount) || 0) - (Number(form.tds) || 0) - (Number(form.deduction) || 0)) * 100) / 100;
                return ba > 0 && gap > 0 ? (
                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800" data-testid="tds-suggest-box">
                    <span>Bill {inr2(ba)} — received is short by <b>{inr2(gap)}</b>.</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 border-amber-400 bg-white text-xs text-amber-800 hover:bg-amber-100"
                      onClick={() => setForm((f) => {
                        const gross = CHARGE_KEYS.reduce((s, ck) => s + (Number(f[ck]) || 0), 0);
                        const next = { ...f, tds_mode: "manual", tds: Math.round(((Number(f.tds) || 0) + gap) * 100) / 100 };
                        if (gross > 0) next.freight_charge = Math.round(((Number(next.freight_charge) || 0) + gap) * 100) / 100;
                        return applyAmount(next);
                      })}
                      data-testid="tds-suggest-btn"
                    >
                      Set {inr2(gap)} as TDS
                    </Button>
                    <span className="text-[10px]">otherwise this MR will show as Part payment</span>
                  </div>
                ) : null;
              })()}
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label className="text-xs">Less: TDS (₹){form.tds_mode === "auto" && form.tds_pct ? ` @ ${form.tds_pct}%` : ""}</Label>
                  <Input
                    type="number"
                    data-testid="receipt-tds"
                    readOnly={form.tds_mode === "auto"}
                    className={form.tds_mode === "auto" ? "bg-slate-100" : ""}
                    value={form.tds}
                    onChange={(e) => setForm((f) => applyAmount({ ...f, tds: Number(e.target.value) }))}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label className="text-xs">Less: Deduction (₹)</Label>
                  <Input
                    type="number"
                    data-testid="receipt-deduction"
                    value={form.deduction}
                    onChange={(e) => setForm((f) => applyAmount({ ...f, deduction: Number(e.target.value) }))}
                  />
                </div>
              </div>
            </div>
            )}

            {(Number(form.deduction) > 0 || (form.bills || []).some((b) => Number(b.deduction) > 0)) && (
              <div className="grid gap-1.5 rounded-md border border-amber-200 bg-amber-50/60 p-3">
                <Label className="text-xs font-semibold text-amber-800">Deduction Reason (required)</Label>
                <Input
                  data-testid="receipt-deduction-reason"
                  placeholder="e.g. Damage claim adjusted / Rate difference / Shortage"
                  className="bg-white"
                  value={form.deduction_reason || ""}
                  onChange={(e) => setForm((f) => ({ ...f, deduction_reason: e.target.value }))}
                />
                {!isAdmin && (
                  <p className="text-[11px] font-medium text-amber-700" data-testid="deduction-approval-note">
                    Deduction MRs need admin approval — an approval request will be sent to admin after saving. The bill clears from your Outstanding Report immediately.
                  </p>
                )}
              </div>
            )}

            {mode === "multi" && (
            <div className="grid gap-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm font-semibold">Bills in this payment</Label>
                <div className="flex items-center gap-2">
                  <Select value="" onValueChange={addBillFromInvoice}>
                    <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="receipt-add-bill-select"><SelectValue placeholder="+ Add existing bill" /></SelectTrigger>
                    <SelectContent>
                      {custInvoices.length === 0 && <SelectItem value="none" disabled>No unpaid bills</SelectItem>}
                      {custInvoices.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {String(i.invoice_no || "").replace(/^INV[-\s]?/i, "")} — {inr2(i.total)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={addBillRow} data-testid="receipt-add-blank-row">
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add row
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={pasteRows} data-testid="receipt-paste-rows">
                    Paste from Excel
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs" data-testid="receipt-bills-table">
                  <thead className="bg-slate-50">
                    <tr className="[&>th]:whitespace-nowrap [&>th]:px-2 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                      <th>Sl</th><th>Branch</th><th>Bill No.</th><th>Bill Date</th><th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th className="text-right">Deduction</th><th className="text-right">TDS</th><th className="text-right">Amount</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.bills || []).length === 0 && (
                      <tr><td colSpan={12} className="px-2 py-6 text-center text-muted-foreground">No bills yet. Add existing bills or a blank row.</td></tr>
                    )}
                    {(form.bills || []).map((b, idx) => (
                      <tr key={idx} className="border-t [&>td]:px-1.5 [&>td]:py-1" data-testid="receipt-bill-row">
                        <td className="text-center">{idx + 1}</td>
                        <td>
                          <Select value={b.branch || "Chennai"} onValueChange={(v) => updateBillRow(idx, "branch", v)}>
                            <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{BRANCHES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td><Input className="h-8 w-[100px] text-xs" value={b.bill_no} onChange={(e) => updateBillRow(idx, "bill_no", e.target.value)} data-testid={`bill-no-${idx}`} /></td>
                        <td><Input type="date" className="h-8 w-[140px] text-xs" value={b.bill_date} onChange={(e) => updateBillRow(idx, "bill_date", e.target.value)} /></td>
                        <td><Input className="h-8 w-[100px] text-xs" value={b.cn_no} onChange={(e) => updateBillRow(idx, "cn_no", e.target.value)} /></td>
                        <td><Input type="date" className="h-8 w-[140px] text-xs" value={b.cn_date} onChange={(e) => updateBillRow(idx, "cn_date", e.target.value)} /></td>
                        <td><Input className="h-8 w-[110px] text-xs" value={b.from_place} onChange={(e) => updateBillRow(idx, "from_place", e.target.value)} data-testid={`bill-from-${idx}`} /></td>
                        <td><Input className="h-8 w-[110px] text-xs" value={b.destination} onChange={(e) => updateBillRow(idx, "destination", e.target.value)} data-testid={`bill-dest-${idx}`} /></td>
                        <td><Input type="number" className="h-8 w-[90px] text-xs text-right" value={b.deduction} onChange={(e) => updateBillRow(idx, "deduction", Number(e.target.value))} data-testid={`bill-deduction-${idx}`} /></td>
                        <td><Input type="number" className="h-8 w-[80px] text-xs text-right" value={b.tds} onChange={(e) => updateBillRow(idx, "tds", Number(e.target.value))} data-testid={`bill-tds-${idx}`} /></td>
                        <td><Input type="number" className="h-8 w-[100px] text-xs text-right" value={b.amount} onChange={(e) => updateBillRow(idx, "amount", Number(e.target.value))} data-testid={`bill-amount-${idx}`} /></td>
                        <td>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeBillRow(idx)} data-testid={`bill-remove-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                {(form.bills || []).length} bill(s) · Net total auto-calculated below (Amount − TDS − Deduction)
              </div>
            </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Received Amount (₹)</Label>
                <Input type="number" data-testid="receipt-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Payment mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                  <SelectTrigger data-testid="receipt-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...MODES, ...(form.payment_mode && !MODES.includes(form.payment_mode) ? [form.payment_mode] : [])].map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.payment_mode === "Cheque" && (
              <div className="grid grid-cols-2 gap-4 rounded-md border border-sky-200 bg-sky-50/60 p-3 sm:grid-cols-4" data-testid="cheque-details">
                <div className="grid gap-1.5">
                  <Label>Cheque No.</Label>
                  <Input data-testid="receipt-cheque-no" value={form.cheque_no || ""} onChange={(e) => setForm({ ...form, cheque_no: e.target.value })} placeholder="e.g. 123456" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Cheque Date</Label>
                  <Input type="date" data-testid="receipt-cheque-date" value={form.cheque_date || ""} onChange={(e) => setForm({ ...form, cheque_date: e.target.value })} />
                </div>
                <div className="grid gap-1.5">
                  <Label>Cheque Amount</Label>
                  <Input type="number" data-testid="receipt-cheque-amount" value={form.cheque_amount || ""} onChange={(e) => setForm({ ...form, cheque_amount: Number(e.target.value) })} placeholder="0" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Bank Name</Label>
                  <Input data-testid="receipt-bank-name" value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="e.g. Punjab National Bank" />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Bill No.</Label>
                <Input data-testid="receipt-bill-no" value={String(form.against_invoice_no || "").replace(/^INV[-\s]?/i, "")} onChange={(e) => setForm({ ...form, against_invoice_no: e.target.value })} placeholder="e.g. 4525" />
              </div>
              <div className="grid gap-1.5">
                <Label>Bill Date</Label>
                <Input type="date" data-testid="receipt-bill-date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>LR No.</Label>
                <Input data-testid="receipt-cn-no" value={form.against_cn_no} onChange={(e) => setForm({ ...form, against_cn_no: e.target.value })} placeholder="e.g. CN-1001" />
              </div>
              <div className="grid gap-1.5">
                <Label>LR Date</Label>
                <Input type="date" data-testid="receipt-cn-date" value={form.cn_date} onChange={(e) => setForm({ ...form, cn_date: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Payment Date</Label>
                <Input type="date" data-testid="receipt-payment-date" value={form.payment_date || ""} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Receipt Date (MR)</Label>
                <Input type="date" data-testid="receipt-mr-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>From</Label>
                <Input value={form.from_place} onChange={(e) => setForm({ ...form, from_place: e.target.value })} placeholder="Chennai" />
              </div>
              <div className="grid gap-1.5">
                <Label>Destination</Label>
                <Input value={form.to_place} onChange={(e) => setForm({ ...form, to_place: e.target.value })} placeholder="Bangalore" />
              </div>
            </div>
            <Textarea placeholder="Narration (optional)" value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-receipt-btn">
              {saving ? "Saving…" : editing ? "Save Changes" : "Record Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={chequeOpen} onOpenChange={setChequeOpen}>
        <DialogContent className={chequeMax ? "h-[96vh] max-h-[96vh] w-[98vw] overflow-y-auto sm:max-w-[98vw]" : "max-h-[85vh] max-w-6xl overflow-y-auto"} data-testid="cheque-register-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8">
              <span>Cheque Register — {tdsPeriod()}</span>
              <span className="flex items-center gap-2">
                {chequeTab === "received" && (
                  <>
                    <Input
                      type="date"
                      className="h-8 w-[140px] text-xs"
                      value={depositDate}
                      onChange={(e) => setDepositDate(e.target.value)}
                      title="Deposit slip date"
                      data-testid="deposit-slip-date"
                    />
                    <Button size="sm" variant="outline" onClick={printSlip} disabled={!chequeRows.length} data-testid="deposit-slip-btn">
                      <Printer className="mr-1.5 h-3.5 w-3.5" /> Deposit Slip
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setBankDelayOpen(true)} disabled={!chequeRows.length} data-testid="bank-delay-btn">
                      Bank Delay
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setNeftOpen(true)} data-testid="neft-only-btn">
                      NEFT-Only List
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={downloadChequeCsv} disabled={chequeTab === "received" ? !chequeRows.length : !issuedRows.length} data-testid="cheque-excel-btn">
                  <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => (chequeTab === "issued" ? printChequeRegister(issuedAsRegisterRows(), tdsPeriod(), ISSUED_OPTS) : printChequeRegister(chequeRowsEnriched, tdsPeriod()))}
                  disabled={chequeTab === "received" ? !chequeRows.length : !issuedRows.length}
                  data-testid="cheque-register-print-btn"
                >
                  <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
                </Button>
                <Button size="sm" variant="outline" className="px-2" onClick={() => setChequeMax((v) => !v)} title={chequeMax ? "Minimize" : "Maximize"} data-testid="cheque-maximize-btn">
                  {chequeMax ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                </Button>
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setChequeTab("received")}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${chequeTab === "received" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid="cheque-tab-received"
            >
              Received (MR) — {chequeRows.length}
            </button>
            <button
              type="button"
              onClick={() => setChequeTab("issued")}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${chequeTab === "issued" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid="cheque-tab-issued"
            >
              Issued (Vouchers + Challans) — {issuedRows.length}
            </button>
          </div>
          {chequeTab === "issued" ? (
            issuedRows.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No cheque payments recorded in Debit Vouchers or Hire Challans yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs" data-testid="issued-cheque-table">
                  <thead className="bg-slate-50">
                    <tr className="[&>th]:whitespace-nowrap [&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                      <th>Source</th><th>Ref No.</th><th>Date</th><th>Paid To</th><th>Cheque No.</th><th>Cheque Date</th><th>Bank Name</th><th className="text-right">Amount</th><th>Clearing Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issuedRows.map((v) => (
                      <tr key={`${v._src}-${v.id}`} className="border-t [&>td]:px-3 [&>td]:py-2" data-testid="issued-cheque-row">
                        <td>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${v._src === "Challan" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`} data-testid="issued-cheque-source">
                            {v._src}
                          </span>
                        </td>
                        <td className="whitespace-nowrap font-semibold">
                          {v._ref || "—"}
                          {v._src === "Challan" && v.vehicle_no ? <span className="ml-1 font-normal text-slate-500">· {v.vehicle_no}</span> : null}
                        </td>
                        <td className="whitespace-nowrap">{v.date ? fmtDate(v.date) : "—"}</td>
                        <td>{v.party_name || "—"}</td>
                        <td className="font-semibold">{v.cheque_no || "—"}</td>
                        <td className="whitespace-nowrap">{v.cheque_date ? fmtDate(v.cheque_date) : "—"}</td>
                        <td>{v.bank_name || "—"}</td>
                        <td className="whitespace-nowrap text-right tabular-nums">{inr2(Number(v.cheque_amount) || Number(v.amount) || 0)}</td>
                        <td>
                          <Select value={v.cheque_status || "Pending"} onValueChange={(s) => (v._src === "Challan" ? setChallanPayChequeStatus(v, s) : setVoucherChequeStatus(v, s))}>
                            <SelectTrigger
                              className={`h-8 w-[120px] text-xs font-semibold ${(v.cheque_status || "Pending") === "Cleared" ? "text-emerald-700" : (v.cheque_status || "Pending") === "Bounced" ? "text-red-700" : "text-amber-700"}`}
                              data-testid="issued-cheque-status-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {["Pending", "Cleared", "Bounced"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : chequeRows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No cheque receipts found for the selected period.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="cheque-register-table">
                <thead className="bg-slate-50">
                  <tr className="[&>th]:whitespace-nowrap [&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                    <th>MR No.</th><th>MR Date</th><th>Customer</th><th>Bill No.</th><th>Bill Date</th><th className="text-right">Bill Amount</th><th>Cheque No.</th><th>Cheque Date</th><th>Bank Name</th><th className="text-right">Amount</th><th>Deposit Date</th><th>Cleared Date</th><th>Clearing Days</th><th>Clearing Status</th><th>Bounce Charge</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {chequeRowsEnriched.map((r) => (
                    <tr key={r.id} className="border-t [&>td]:px-3 [&>td]:py-2" data-testid="cheque-register-row">
                      <td className="font-semibold">{String(r.mr_no || "—").replace(/^MR[-\s]?/i, "")}</td>
                      <td className="whitespace-nowrap">{r.date ? fmtDate(r.date) : "—"}</td>
                      <td>
                        {r.customer_name || "—"}
                        {r._repeat && (
                          <span
                            className="ml-1.5 whitespace-nowrap rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700"
                            title={`${bounceCountByCust[r.customer_name]} bounced cheques on record — insist on NEFT/RTGS`}
                            data-testid="repeat-bouncer-badge"
                          >
                            ⚠ {bounceCountByCust[r.customer_name]}× bounced — prefer NEFT
                          </span>
                        )}
                      </td>
                      {(() => {
                        const b = billInfo(r);
                        return (
                          <>
                            <td className="whitespace-nowrap font-semibold" data-testid="cheque-bill-no">{b.no}</td>
                            <td className="whitespace-nowrap" data-testid="cheque-bill-date">{b.date ? fmtDate(b.date) : "—"}</td>
                            <td className="whitespace-nowrap text-right tabular-nums" data-testid="cheque-bill-amount">{b.amount ? inr2(b.amount) : "—"}</td>
                          </>
                        );
                      })()}
                      <td className="font-semibold">{r.cheque_no || "—"}</td>
                      <td className="whitespace-nowrap">{r.cheque_date ? fmtDate(r.cheque_date) : "—"}</td>
                      <td>{r.bank_name || "—"}</td>
                      <td className="whitespace-nowrap text-right tabular-nums">{inr2(Number(r.cheque_amount) || Number(r.amount) || 0)}</td>
                      <td>
                        <Input
                          type="date"
                          className="h-8 w-[140px] text-xs"
                          value={(chequeEdits[r.id]?.deposit_date ?? r.deposit_date) || ""}
                          onChange={(e) => setChequeEdits((m) => ({ ...m, [r.id]: { ...m[r.id], deposit_date: e.target.value } }))}
                          data-testid="cheque-deposit-date"
                        />
                      </td>
                      <td>
                        <Input
                          type="date"
                          className="h-8 w-[140px] text-xs"
                          value={(chequeEdits[r.id]?.cleared_date ?? r.cleared_date) || ""}
                          onChange={(e) => setChequeEdits((m) => ({ ...m, [r.id]: { ...m[r.id], cleared_date: e.target.value } }))}
                          data-testid="cheque-cleared-date"
                        />
                      </td>
                      <td>
                        {(() => {
                          const cd = clearingDays(r);
                          if (!cd) return <span className="text-muted-foreground">—</span>;
                          const cls = cd.pending
                            ? cd.days > 5
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                            : cd.days > 5
                            ? "bg-amber-100 text-amber-700"
                            : "bg-emerald-100 text-emerald-700";
                          return (
                            <span
                              className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold ${cls}`}
                              title={cd.pending ? `${cd.days} day(s) since deposit — still not cleared` : `Cleared in ${cd.days} day(s)`}
                              data-testid="clearing-days-chip"
                            >
                              {cd.days}d{cd.pending ? " …" : ""}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        <Select value={r.cheque_status || "Pending"} onValueChange={(v) => setChequeStatus(r, v)}>
                          <SelectTrigger
                            className={`h-8 w-[120px] text-xs font-semibold ${(r.cheque_status || "Pending") === "Cleared" ? "text-emerald-700" : (r.cheque_status || "Pending") === "Bounced" ? "text-red-700" : "text-amber-700"}`}
                            data-testid="cheque-status-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {["Pending", "Cleared", "Bounced"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        {r.cheque_status === "Bounced" ? (
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              className="h-8 w-[90px] text-right text-xs font-semibold text-red-700"
                              defaultValue={Number(r.bounce_charge) || ""}
                              placeholder="Bank charge"
                              onBlur={(e) => {
                                const v = Number(e.target.value) || 0;
                                if (v !== (Number(r.bounce_charge) || 0)) setChequeField(r, "bounce_charge", v);
                              }}
                              data-testid="cheque-bounce-charge"
                            />
                            {Number(r.bounce_charge) > 0 && (
                              <button
                                type="button"
                                className={`rounded-full px-2 py-1 text-[10px] font-bold ${r.bounce_charge_recovered ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                                title={r.bounce_charge_recovered ? "Bounce charge recovered — click to mark as due again" : "Bounce charge added to customer dues — click when recovered"}
                                onClick={() => setChequeField(r, "bounce_charge_recovered", !r.bounce_charge_recovered)}
                                data-testid="bounce-charge-recovered-toggle"
                              >
                                {r.bounce_charge_recovered ? "Recovered ✓" : "In Dues"}
                              </button>
                            )}
                            {Number(r.bounce_charge) > 0 && (
                              <button
                                type="button"
                                className="grid h-7 w-7 cursor-pointer place-items-center rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                title="WhatsApp the customer about this bounce & charges"
                                onClick={() => bounceWa(r)}
                                data-testid="bounce-charge-wa-btn"
                              >
                                <MessageCircle className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {r.reversed ? (
                              <div className="flex flex-col items-start gap-0.5">
                                <button
                                  type="button"
                                  className="rounded-full bg-slate-700 px-2 py-1 text-[10px] font-bold text-white hover:bg-slate-800"
                                  onClick={() => reverseReceipt(r, true)}
                                  title="Reversed — click to undo"
                                  data-testid="cheque-undo-reverse-btn"
                                >
                                  Reversed · Undo
                                </button>
                                <span className="whitespace-nowrap text-[10px] text-slate-500" data-testid="cheque-reversal-log">
                                  by {r.reversed_by || "—"}{r.reversed_at ? ` · ${fmtDate(String(r.reversed_at).slice(0, 10))}` : ""}
                                </span>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="rounded-full bg-red-600 px-2 py-1 text-[10px] font-bold text-white hover:bg-red-700"
                                onClick={() => reverseReceipt(r)}
                                title="Reverse this receipt — bill goes back to unpaid"
                                data-testid="cheque-reverse-btn"
                              >
                                Reverse
                              </button>
                            )}
                            {r.replacement ? (
                              <div className="flex items-center gap-1" data-testid="replacement-info">
                                <span className="whitespace-nowrap text-[10px] font-semibold text-slate-600">
                                  Repl: {r.replacement.cheque_no}{r.replacement.bank_name ? ` · ${r.replacement.bank_name}` : ""} · {inr(Number(r.replacement.cheque_amount) || 0)}
                                </span>
                                <Select value={r.replacement.status || "Pending"} onValueChange={(v) => setReplStatus(r, v)}>
                                  <SelectTrigger
                                    className={`h-7 w-[100px] text-[11px] font-semibold ${r.replacement.status === "Cleared" ? "text-emerald-700" : r.replacement.status === "Bounced" ? "text-red-700" : "text-amber-700"}`}
                                    data-testid="replacement-status-select"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {["Pending", "Cleared", "Bounced"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="whitespace-nowrap rounded-full border border-sky-300 bg-sky-50 px-2 py-1 text-[10px] font-bold text-sky-700 hover:bg-sky-100"
                                onClick={() => {
                                  setReplForm({ cheque_no: "", cheque_date: "", cheque_amount: Number(r.cheque_amount) || Number(r.amount) || "", bank_name: "" });
                                  setReplTarget(r);
                                }}
                                title="Record the replacement cheque given for this bounce"
                                data-testid="add-replacement-btn"
                              >
                                + Replacement
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          className={`h-8 px-3 text-xs font-bold ${chequeEdits[r.id] ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                          variant={chequeEdits[r.id] ? "default" : "outline"}
                          onClick={() => saveChequeRow(r)}
                          data-testid="cheque-save-btn"
                        >
                          <Save className="mr-1 h-3.5 w-3.5" /> Save
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bankDelayOpen} onOpenChange={setBankDelayOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[760px]" data-testid="bank-delay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8">
              <span>Bank Delay Report — Avg. Clearing Days</span>
              <Button size="sm" variant="outline" onClick={() => printBankDelayReport(bankDelayStats(), tdsPeriod())} data-testid="bank-delay-print-btn">
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="rounded border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr className="text-left [&>th]:p-2">
                  <th>Bank</th><th className="text-right">Deposited</th><th className="text-right">Cleared</th><th className="text-right">Avg. Days</th><th className="text-right">Fastest</th><th className="text-right">Slowest</th><th className="text-right">Pending</th>
                </tr>
              </thead>
              <tbody>
                {bankDelayStats().map((g, i) => (
                  <tr key={g.bank} className={`border-t [&>td]:p-2 ${i === 0 && g.avg != null ? "bg-emerald-50" : ""}`} data-testid="bank-delay-row">
                    <td className="font-semibold">{g.bank}{i === 0 && g.avg != null && <span className="ml-1.5 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold text-white">FASTEST</span>}</td>
                    <td className="text-right tabular-nums">{g.total}</td>
                    <td className="text-right tabular-nums">{g.cleared}</td>
                    <td className="text-right font-bold tabular-nums">{g.avg != null ? `${g.avg.toFixed(1)}d` : "—"}</td>
                    <td className="text-right tabular-nums">{g.min != null ? `${g.min}d` : "—"}</td>
                    <td className="text-right tabular-nums">{g.max != null ? `${g.max}d` : "—"}</td>
                    <td className="text-right tabular-nums text-amber-700">{g.pending || "—"}</td>
                  </tr>
                ))}
                {bankDelayStats().length === 0 && (
                  <tr><td colSpan={7} className="p-4 text-center text-muted-foreground" data-testid="bank-delay-empty">No cheques with Deposit Date set — set deposit dates in the Cheque Register first.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground">Ranked fastest first. Average uses cheques marked Cleared with both Deposit &amp; Cleared dates ({tdsPeriod()}).</p>
        </DialogContent>
      </Dialog>

      <Dialog open={neftOpen} onOpenChange={setNeftOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[720px]" data-testid="neft-only-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8">
              <span>NEFT-Only Customer List</span>
              <Button size="sm" variant="outline" onClick={() => printNeftOnlyList(neftListRows())} data-testid="neft-only-print-btn">
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-[11px] font-semibold text-red-700">
            ⚠ Do not accept cheques from these customers (2+ bounces on record) — insist on NEFT / RTGS / UPI only.
          </div>
          <div className="rounded border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr className="text-left [&>th]:p-2">
                  <th>Customer</th><th>Phone</th><th className="text-right">Bounces</th><th className="text-right">Bounced Amt</th><th>Bank(s)</th><th>Last Bounce</th>
                </tr>
              </thead>
              <tbody>
                {neftListRows().map((r) => (
                  <tr key={r.customer} className="border-t [&>td]:p-2" data-testid="neft-only-row">
                    <td className="font-semibold">{r.customer}</td>
                    <td>{r.phone || "—"}</td>
                    <td className="text-right font-bold tabular-nums text-red-700">{r.bounces}</td>
                    <td className="text-right tabular-nums">{inr(r.amount)}</td>
                    <td>{r.banks || "—"}</td>
                    <td>{r.last ? fmtDate(r.last) : "—"}</td>
                  </tr>
                ))}
                {neftListRows().length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-center text-muted-foreground" data-testid="neft-only-empty">No repeat bouncers — all customers clear. 🎉</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={scoreOpen} onOpenChange={setScoreOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[820px]" data-testid="payment-score-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8">
              <span>Customer Payment Score</span>
              <Button size="sm" variant="outline" onClick={() => printPaymentScores(paymentScores())} data-testid="payment-score-print-btn">
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground">
            <span className="font-bold text-emerald-700">A</span> = pays clean &amp; on time · <span className="font-bold text-amber-700">B</span> = 1 bounce or 31–60 day avg delay · <span className="font-bold text-red-700">C</span> = 2+ bounces or &gt;60 day avg delay (delay = Bill Date → MR Date)
          </p>
          <div className="rounded border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr className="text-left [&>th]:p-2">
                  <th>Customer</th><th className="text-right">Receipts</th><th className="text-right">Bounces</th><th className="text-right">Avg. Delay</th><th className="text-center">Score</th><th>Why</th>
                </tr>
              </thead>
              <tbody>
                {paymentScores().map((r) => (
                  <tr key={r.customer} className="border-t [&>td]:p-2" data-testid="payment-score-row">
                    <td className="font-semibold">{r.customer}</td>
                    <td className="text-right tabular-nums">{r.receipts}</td>
                    <td className={`text-right tabular-nums ${r.bounces ? "font-bold text-red-700" : ""}`}>{r.bounces || "—"}</td>
                    <td className="text-right tabular-nums">{r.avgDelay != null ? `${r.avgDelay}d` : "—"}</td>
                    <td className="text-center">
                      <span className={`inline-block min-w-[26px] rounded-full px-2 py-0.5 text-[11px] font-bold text-white ${r.score === "A" ? "bg-emerald-600" : r.score === "B" ? "bg-amber-600" : "bg-red-600"}`}>{r.score}</span>
                    </td>
                    <td className="text-[11px] text-muted-foreground">{r.reason}</td>
                  </tr>
                ))}
                {paymentScores().length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-center text-muted-foreground" data-testid="payment-score-empty">No receipts to score yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
